import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  Suspense,
  lazy,
} from "react";
import { createPortal } from "react-dom";
import { Routes, Route, Link } from "react-router-dom";
import {
  Book,
  Target,
  Users,
  Lightbulb,
  FileText,
  Download,
  CheckCircle,
  Lock,
  Unlock,
  RefreshCw,
  AlertCircle,
  Calendar,
  LogOut,
  RotateCcw,
  Mail,
  Menu,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import Login from "./Login";
import logo from "./Assets/logo-icon.webp";
import heroLandscape from "./Assets/hero-landscape.webp";
import {
  trackButtonClick,
  trackFormStart,
  trackFormComplete,
  trackFormAbandon,
  trackEbookGeneration,
  trackBeginCheckout,
  trackAddToCart,
  trackPurchase,
  trackUpgradeInterest,
  trackDemoInteraction,
  trackPageView,
  trackScrollWithThrottle,
  trackFormInteraction,
  trackSessionStart,
  trackSessionEnd,
} from "./utils/analytics";
import logger from "./utils/logger";

// Lazy load legal components to reduce initial bundle size
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./components/RefundPolicy"));
const Disclaimer = lazy(() => import("./components/Disclaimer"));

// Lazy load About and Contact pages
const AboutPage = lazy(() => import("./components/AboutPage"));
const ContactPage = lazy(() => import("./components/ContactPage"));
import SharedHeader from "./components/SharedHeader";
import EbookOrbitShowcase from "./components/EbookOrbitShowcase";
import RouteMeta from "./seo/RouteMeta";

// Fades the hero's video and overlays out over its last stretch, so the hero
// dissolves into the page background instead of ending on a hard line. Only
// the background layers are masked; the hero text and button are not.
const HERO_LAYER_FADE = {
  maskImage: "linear-gradient(to bottom, #000 0%, #000 72%, transparent 100%)",
  WebkitMaskImage:
    "linear-gradient(to bottom, #000 0%, #000 72%, transparent 100%)",
  maskRepeat: "no-repeat",
  WebkitMaskRepeat: "no-repeat",
};

// The hero video runs 2px past the hero's top and bottom; the section's
// overflow-hidden clips that away. On a fresh load the browser can draw a
// one-pixel row of the video's edge (a purple-to-blue hairline at the seam);
// before the blend an opaque overlay hid it, now the edge is simply off-screen.
const HERO_VIDEO_BLEED = {
  top: "-2px",
  height: "calc(100% + 4px)",
};

const ModalPortal = ({ children }) => {
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(children, document.body);
};

const EbookPlanningTool = () => {
  // State for background animation
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [stepsLoaded, setStepsLoaded] = useState(false);

  // Get auth context first - must be at the top before any early returns
  const {
    currentUser,
    logout,
    activateProWithPayPal,
    loading: authLoading,
    authInitialized,
    isPro: authIsPro,
    proLifetime: authProLifetime,
    proExpiryDate: authProExpiryDate,
    proDaysRemaining: authProDaysRemaining,
    accountEmail: authAccountEmail,
    getVerificationBlockingState,
  } = useAuth();

  logger.log("✅ Auth ready, rendering main app:", {
    authLoading,
    authInitialized,
  });

  // Track session start and scroll events when auth is ready
  useEffect(() => {
    if (authInitialized && !authLoading) {
      const sessionStartedAt = Date.now();
      trackSessionStart();

      // Track scroll events for engagement analysis
      const handleScroll = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = Math.round((scrollTop / docHeight) * 100);

        if (scrollPercent >= 50) {
          trackScrollWithThrottle(scrollPercent);
        }
      };

      window.addEventListener("scroll", handleScroll);

      // Track page view for SPA navigation
      trackPageView("main_app", "ebook_planner");

      return () => {
        try {
          window.removeEventListener("scroll", handleScroll);
          // Track session end when component unmounts, with the time spent
          // since the session started (GA param session_duration_seconds).
          trackSessionEnd(Math.round((Date.now() - sessionStartedAt) / 1000));
        } catch (error) {
          // Silently handle cleanup errors
        }
      };
    }
  }, [authInitialized, authLoading]);

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    topic: "",
    audience: "",
    genre: "",
    chapters: 3, // Start with 3 chapters for free users
    tone: "professional",
    keywords: "",
    title: "",
    description: "",
  });
  const [generatedOutline, setGeneratedOutline] = useState("");
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [isPro, setIsPro] = useState(false);
  const [, setShowUpgrade] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showPricingPlans, setShowPricingPlans] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [appNotice, setAppNotice] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [restoreError, setRestoreError] = useState("");
  const [stepError, setStepError] = useState("");
  const [proExpiryDate, setProExpiryDate] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isLifetimePro, setIsLifetimePro] = useState(false);
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [freeCardGlow, setFreeCardGlow] = useState(false);
  const [proCardGlow, setProCardGlow] = useState(false);
  const [, setIsGuestUser] = useState(false); // Track guest users using free features
  const [isScrolled, setIsScrolled] = useState(false);
  const [savedProgressData, setSavedProgressData] = useState(null);
  const [showProgressRestorePrompt, setShowProgressRestorePrompt] =
    useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const appNoticeTimeoutRef = useRef(null);
  const saveTimerRef = useRef(null);
  const progressLoadedRef = useRef(false);
  const restoredChaptersRef = useRef(null);
  // Pro entitlement is server-authoritative via AuthContext. Local Pro state is
  // kept for legacy PayPal restore display but can only ADD Pro, never remove a
  // server-true entitlement.
  const effectiveIsPro = authIsPro || isPro;
  const effectiveIsLifetimePro = authProLifetime || isLifetimePro;
  const effectiveDaysRemaining =
    typeof authProDaysRemaining === "number" && authProDaysRemaining > 0
      ? authProDaysRemaining
      : daysRemaining;
  const effectiveProExpiryDate = authProExpiryDate || proExpiryDate;
  const proStatusLabel = effectiveIsLifetimePro
    ? "LIFETIME ACCESS"
    : `${effectiveDaysRemaining} DAYS REMAINING`;
  const proPlanStatusLabel = effectiveIsLifetimePro
    ? "Lifetime access"
    : `${effectiveDaysRemaining} days remaining`;
  const signedInEmailLabel =
    currentUser?.email || authAccountEmail || "Signed in";
  const signedInProStatusLabel = effectiveIsLifetimePro
    ? "Lifetime"
    : effectiveDaysRemaining > 0
      ? `${effectiveDaysRemaining}d`
      : "Active";
  const signedInPlanLabel = effectiveIsPro
    ? `PRO • ${signedInProStatusLabel}`
    : "FREE";

  // Track form abandonment when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentStep > 0 && steps && currentStep < steps.length - 1) {
        trackFormAbandon(currentStep, formData);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStep, formData]);

  // Trigger background fade-in animation on mount
  useEffect(() => {
    // Start background animation immediately with very low opacity
    setBackgroundLoaded(true);

    // Trigger content fade-in after background
    setTimeout(() => {
      setContentLoaded(true);
    }, 600);

    // Trigger steps fade-in after content
    setTimeout(() => {
      setStepsLoaded(true);
    }, 1000);
  }, []);

  // Track scroll position for header background effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 1);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Start at the top without delayed scroll timers that fight the first mobile swipe.
  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (localStorage.getItem("scrollToTop") === "true") {
      localStorage.removeItem("scrollToTop");
    }

    window.scrollTo(0, 0);

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMenu && !event.target.closest(".mobile-menu-container")) {
        trackButtonClick("close_mobile_menu_outside", "mobile_menu");
        setShowMobileMenu(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showMobileMenu) {
        trackButtonClick("close_mobile_menu_escape", "mobile_menu");
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent body scroll when menu is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [showMobileMenu]);

  // Handle modal exit functionality for pricing plans modal
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (
        showPricingPlans &&
        event.target.classList.contains("modal-backdrop")
      ) {
        trackButtonClick("close_pricing_modal_outside", "pricing_modal");
        setShowPricingPlans(false);
      }
    };

    // Force modal content to scroll to top when modal opens
    if (showPricingPlans) {
      // First, scroll the main window to top to ensure modal appears centered
      window.scrollTo({ top: 0, behavior: "instant" });

      // Then scroll modal content to top
      setTimeout(() => {
        const modalContent = document.querySelector(".modal-content");
        if (modalContent) {
          modalContent.scrollTop = 0;
          // Ensure smooth scrolling for future scrolls
          modalContent.style.scrollBehavior = "smooth";
          // Force the modal to be scrollable
          modalContent.style.overflowY = "auto";
        }
      }, 50); // Small delay to ensure modal is rendered
    }

    const handleModalEscapeKey = (event) => {
      if (event.key === "Escape" && showPricingPlans) {
        trackButtonClick("close_pricing_modal_escape", "pricing_modal");
        setShowPricingPlans(false);
      }
    };

    if (showPricingPlans) {
      document.addEventListener("mousedown", handleModalClickOutside);
      document.addEventListener("keydown", handleModalEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleModalClickOutside);
      document.removeEventListener("keydown", handleModalEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [showPricingPlans]);

  // PayPal payment link with return URL
  const PAYPAL_LINK = "https://www.paypal.com/ncp/payment/LAY3ZLW92872U";

  const clearProState = () => {
    setIsPro(false);
    setUserEmail("");
    setProExpiryDate(null);
    setDaysRemaining(0);
    setIsLifetimePro(false);
  };

  const progressKey = (uid) => `ebookPlanner_progress_${uid}`;

  const saveProgressToStorage = (uid, data) => {
    try {
      localStorage.setItem(
        progressKey(uid),
        JSON.stringify({ ...data, savedAt: new Date().toISOString() })
      );
    } catch (err) {
      logger.error("Failed to save progress:", err);
    }
  };

  const clearProgressFromStorage = (uid) => {
    try {
      localStorage.removeItem(progressKey(uid));
    } catch (err) {
      logger.error("Failed to clear progress:", err);
    }
  };

  const showAppNotice = (message, type = "success") => {
    if (appNoticeTimeoutRef.current) {
      window.clearTimeout(appNoticeTimeoutRef.current);
    }

    setAppNotice({ message, type });
    appNoticeTimeoutRef.current = window.setTimeout(() => {
      setAppNotice(null);
      appNoticeTimeoutRef.current = null;
    }, 3500);
  };

  const dismissAppNotice = () => {
    if (appNoticeTimeoutRef.current) {
      window.clearTimeout(appNoticeTimeoutRef.current);
      appNoticeTimeoutRef.current = null;
    }
    setAppNotice(null);
  };

  useEffect(() => {
    return () => {
      if (appNoticeTimeoutRef.current) {
        window.clearTimeout(appNoticeTimeoutRef.current);
      }
    };
  }, []);

  const handleSignOut = async (buttonName, locationName) => {
    trackButtonClick(buttonName, locationName);
    try {
      await logout();
      clearProState();
      setShowMobileMenu(false);
      showAppNotice("Signed out successfully.");
    } catch (error) {
      logger.error("Sign out error:", error);
      showAppNotice("Failed to sign out. Please try again.", "error");
    }
  };

  const scrollTowardModalCenter = () => {
    const scrollTarget = Math.max(
      0,
      Math.round(window.scrollY + Math.min(window.innerHeight * 0.3, 280))
    );

    window.scrollTo({ top: scrollTarget, behavior: "smooth" });
  };

  const openLoginModal = () => {
    setShowMobileMenu(false);
    scrollTowardModalCenter();
    setShowLogin(true);
  };

  const openRestoreModal = ({
    clearFields = false,
    closePricing = false,
    closePaymentSuccess = false,
  } = {}) => {
    setShowMobileMenu(false);
    if (closePricing) setShowPricingPlans(false);
    if (closePaymentSuccess) setShowPaymentSuccess(false);
    if (clearFields) {
      setUserEmail("");
      setVerificationCode("");
    }
    setRestoreError("");
    scrollTowardModalCenter();
    setShowRestore(true);
  };

  useEffect(() => {
    if (!showLogin) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      const firstInput = document.querySelector(
        "[data-login-modal-panel] input"
      );
      if (firstInput) {
        firstInput.focus({ preventScroll: true });
      }
    }, 260);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [showLogin]);

  useEffect(() => {
    if (!showRestore) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      const firstInput = document.querySelector(
        "[data-restore-modal-panel] input"
      );
      if (firstInput) {
        firstInput.focus({ preventScroll: true });
      }
    }, 260);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [showRestore]);

  // Close the login modal as soon as the auth context reports a signed-in
  // user, and handle the PayPal payment-success callback.
  useEffect(() => {
    if (currentUser) {
      setShowLogin(false);
    } else {
      clearProState();
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment") === "success") {
      handlePaymentSuccess();
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [currentUser]);

  // Mirror the server-authoritative entitlement from AuthContext into local
  // display state. The server is the source of truth: when it says Pro is
  // false, we must clear the local mirror so stale Pro state from a previous
  // session cannot mask a downgrade, expiry, or revoked entitlement.
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (authIsPro) {
      setIsPro(true);
      setIsLifetimePro(Boolean(authProLifetime));
      setProExpiryDate(authProExpiryDate || null);
      setDaysRemaining(
        authProLifetime
          ? 0
          : typeof authProDaysRemaining === "number"
            ? authProDaysRemaining
            : 0
      );
      setUserEmail(authAccountEmail || currentUser.email || "");
      return;
    }

    // Server entitlement is false: clear local Pro mirror.
    setIsPro(false);
    setIsLifetimePro(false);
    setProExpiryDate(null);
    setDaysRemaining(0);
  }, [
    currentUser,
    authIsPro,
    authProLifetime,
    authProExpiryDate,
    authProDaysRemaining,
    authAccountEmail,
  ]);

  // Enforce chapter limits when effective Pro status changes (auth or local).
  // A saved session resumed while not Pro is capped in the Resume handler,
  // which keeps the saved count in restoredChaptersRef; if Pro status arrives
  // afterwards (the entitlement check finishes after first render), the saved
  // count is given back so Pro users are never capped by that race.
  useEffect(() => {
    if (!effectiveIsPro && formData.chapters > 3) {
      setFormData((prev) => ({ ...prev, chapters: 3 }));
    } else if (effectiveIsPro && restoredChaptersRef.current) {
      const restoredChapters = restoredChaptersRef.current;
      setFormData((prev) => ({ ...prev, chapters: restoredChapters }));
    }
    if (effectiveIsPro) {
      restoredChaptersRef.current = null;
    }
  }, [effectiveIsPro]); // Remove formData.chapters to prevent infinite loops

  // Load saved progress, runs once on mount for guests, once per sign-in for users
  useEffect(() => {
    if (progressLoadedRef.current) return;
    progressLoadedRef.current = true;

    const key = currentUser
      ? `ebookPlanner_progress_${currentUser.uid}`
      : "ebookPlanner_progress_guest";

    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.currentStep > 0 || saved.formData?.topic) {
          setSavedProgressData(saved);
          setShowProgressRestorePrompt(true);
        }
      }
    } catch (err) {
      logger.error("Failed to load saved progress:", err);
    }
  }, [currentUser]); // re-runs when auth state changes so signed-in users also get their data

  // When user signs out, reset the load guard so next session can load guest progress
  useEffect(() => {
    if (!currentUser) {
      progressLoadedRef.current = false;
    }
  }, [currentUser]);

  // Auto-save progress to localStorage for both guests and signed-in users
  useEffect(() => {
    if (currentStep === 0 && !formData.topic) return;

    const storageId = currentUser ? currentUser.uid : "guest";

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveProgressToStorage(storageId, {
        formData,
        currentStep,
        generatedOutline,
        generatedPrompts,
      });
    }, 2000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [currentUser, formData, currentStep, generatedOutline, generatedPrompts]); // eslint-disable-line

  // Handle payment success (when returning from PayPal)
  const handlePaymentSuccess = () => {
    setShowPaymentSuccess(true);
  };

  if (authLoading || !authInitialized) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <img
            src={logo}
            alt="AI Ebook Planner"
            className="w-24 h-24 mx-auto object-contain animate-pulse mb-6"
          />
          <div className="flex items-center justify-center mb-4">
            <RefreshCw className="animate-spin mr-3" size={24} />
            <span className="text-xl font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Initializing secure authentication...
            </span>
          </div>
          <p className="text-gray-400 text-sm">
            Restoring your protected account session
          </p>
        </div>
      </div>
    );
  }

  const verificationBlockingState = currentUser
    ? getVerificationBlockingState()
    : { blocked: false, reason: null };

  // Handle restore purchase with Firebase integration
  const handleRestorePurchase = async () => {
    const paypalEmail = userEmail.trim();
    const transactionId = verificationCode.trim();
    const missingEmail = !paypalEmail;
    const missingTransactionId = !transactionId;

    if (missingEmail || missingTransactionId) {
      if (missingEmail && missingTransactionId) {
        setRestoreError("Enter your PayPal email and transaction ID.");
      } else if (missingEmail) {
        setRestoreError("Enter your PayPal email address.");
      } else {
        setRestoreError("Enter your PayPal transaction ID.");
      }

      const missingInputSelector = missingEmail
        ? "[data-restore-email-input]"
        : "[data-restore-transaction-input]";
      window.setTimeout(() => {
        document
          .querySelector(missingInputSelector)
          ?.focus({ preventScroll: true });
      }, 0);
      return;
    }

    setRestoreError("");

    if (!currentUser) {
      alert("Please sign in first to restore your Pro access.");
      openLoginModal();
      return;
    }

    try {
      const result = await activateProWithPayPal(paypalEmail, transactionId);

      if (result.success) {
        // Track successful purchase
        trackPurchase(transactionId, 19.0, "USD");

        setIsPro(true);
        setIsLifetimePro(result.lifetime === true);
        setUserEmail(paypalEmail);
        setProExpiryDate(
          result.expiryDate ? new Date(result.expiryDate) : null
        );
        setDaysRemaining(result.lifetime ? 0 : result.daysRemaining || 0);
        setShowRestore(false);
        setShowPaymentSuccess(false);

        alert(
          result.lifetime
            ? "Pro access activated with lifetime access."
            : `Pro access activated. You have ${result.daysRemaining} days remaining.`
        );
      } else {
        alert(result.message);
      }
    } catch (error) {
      logger.error("Restore purchase error:", error);
      alert("Unable to restore purchase. Please try again.");
    }
  };

  const steps = [
    {
      icon: Target,
      title: "Topic & Niche",
      description: "Define your ebook focus",
    },
    {
      icon: Users,
      title: "Target Audience",
      description: "Identify your readers",
    },
    { icon: Book, title: "Book Structure", description: "Plan your content" },
    {
      icon: Lightbulb,
      title: "AI Prompts",
      description: "Generate content prompts",
    },
    { icon: FileText, title: "Final Prompt", description: "Copy & generate" },
  ];

  const genres = [
    "Academic & Study Guides",
    "Aging & Senior Health",
    "Alternative Medicine & Healing",
    "Art & Drawing",
    "Artificial Intelligence & Machine Learning",
    "Business & Entrepreneurship",
    "Career Development",
    "Child Development",
    "Confidence & Self-Esteem",
    "Consulting & Freelancing",
    "Cooking & Recipes",
    "Crafts & DIY",
    "Cryptocurrency & Blockchain",
    "Cybersecurity",
    "Data Science & Analytics",
    "Digital Marketing",
    "E-commerce & Online Business",
    "Fiction & Creative Writing",
    "Finance & Investment",
    "Gardening & Homesteading",
    "Goal Setting & Achievement",
    "Graphic Design & Creative Arts",
    "Habits & Behavior Change",
    "Health & Fitness",
    "History & Biography",
    "Homeschooling & Education",
    "How-To & Instructional",
    "Interior Design & Home Decor",
    "Law & Legal Advice",
    "Leadership & Management",
    "Marketing & Sales",
    "Men's Health",
    "Mental Health & Psychology",
    "Mental Health & Wellness",
    "Mindfulness & Meditation",
    "Minimalism & Simple Living",
    "Music & Audio Production",
    "Parenting & Family",
    "Pet Care & Training",
    "Photography",
    "Poetry & Literature",
    "Politics & Current Events",
    "Pregnancy & Childbirth",
    "Productivity & Time Management",
    "Real Estate Investing",
    "Relationships & Dating",
    "Science & Research",
    "Self-Help & Personal Development",
    "Senior Living & Retirement",
    "Social Media Marketing",
    "Spirituality & Religion",
    "Sports & Recreation",
    "Sustainability & Eco-Living",
    "Technology & Programming",
    "Teen & Young Adult Issues",
    "Travel & Adventure",
    "Web Development",
    "Weight Loss & Nutrition",
    "Women's Health",
    "Yoga & Pilates",
  ];

  const tones = [
    { value: "professional", label: "Professional & Authoritative" },
    { value: "friendly", label: "Friendly & Conversational" },
    { value: "casual", label: "Casual & Relaxed" },
    { value: "inspiring", label: "Inspiring & Motivational" },
    { value: "academic", label: "Academic & Detailed" },
  ];

  // Validate Pro access before allowing Pro features. Pulls from the effective
  // (auth or local) entitlement so server-issued Pro and PayPal restore both
  // unlock features.
  const validateProAccess = () => {
    if (!effectiveIsPro) return false;

    if (effectiveIsLifetimePro) {
      return true;
    }

    if (
      !effectiveProExpiryDate ||
      effectiveProExpiryDate.getTime() <= Date.now()
    ) {
      clearProState();
      setFormData((prev) => ({
        ...prev,
        chapters: Math.min(prev.chapters, 3),
      }));
      alert(
        "Your 30 days of Pro access ended during this session. Renew Pro to keep using Pro features."
      );
      return false;
    }

    return true;
  };

  // Generate smart fallback chapter titles based on topic and genre
  // Soften hype-heavy AI-generated chapter titles when the user's own topic
  // didn't reach for those numbers. Example: a "$1k/month passive income"
  // book shouldn't end with "Scaling to Six Figures" unless the user asked
  // for it. The pass is purely cosmetic, content is still up to the AI.
  const sanitizeChapterTitles = (titles, sourceText) => {
    if (!Array.isArray(titles)) return titles;
    const haystack = String(sourceText || "").toLowerCase();
    const userExplicitlyMentionsBigNumbers =
      /six[\s-]?figure|seven[\s-]?figure|\$?100k|\$?1m|millionaire|million dollar/.test(
        haystack
      );

    return titles.map((title) => {
      if (typeof title !== "string") return title;
      let cleaned = title;
      if (!userExplicitlyMentionsBigNumbers) {
        cleaned = cleaned
          .replace(
            /scaling\s+to\s+six[\s-]?figures?/gi,
            "Scaling Beyond Your First Goal"
          )
          .replace(
            /scaling\s+to\s+seven[\s-]?figures?/gi,
            "Scaling Beyond Your First Goal"
          )
          .replace(/six[\s-]?figure(s)?/gi, "next-level")
          .replace(/seven[\s-]?figure(s)?/gi, "next-level")
          .replace(/become\s+a\s+millionaire/gi, "grow your earnings further")
          .replace(/millionaire/gi, "higher-earning entrepreneur");
      }
      cleaned = cleaned
        .replace(/get\s+rich\s+quick/gi, "grow your earnings")
        .replace(/get\s+rich/gi, "grow your earnings")
        .replace(/guaranteed/gi, "reliable")
        .replace(/overnight\s+success/gi, "early momentum")
        .replace(/overnight/gi, "step by step")
        .replace(/while\s+you\s+sleep/gi, "with less daily effort");
      return cleaned.replace(/\s+/g, " ").trim();
    });
  };

  const generateFallbackChapterTitles = (topic, genre, numChapters) => {
    const genericTemplates = {
      "Business & Entrepreneurship": [
        "Understanding the Fundamentals",
        "Building Your Foundation",
        "Developing Your Strategy",
        "Implementation and Execution",
        "Scaling and Growth",
        "Overcoming Challenges",
        "Advanced Techniques",
        "Case Studies and Success Stories",
        "Future Trends and Opportunities",
        "Your Action Plan",
      ],
      "Health & Fitness": [
        "Getting Started: Your Journey Begins",
        "Understanding the Science",
        "Essential Nutrition Guidelines",
        "Building Effective Habits",
        "Core Training Principles",
        "Advanced Strategies",
        "Tracking Progress and Results",
        "Common Mistakes to Avoid",
        "Maintaining Long-term Success",
        "Your Personal Transformation",
      ],
      "Self-Help & Personal Development": [
        "Discovering Your Potential",
        "Breaking Through Barriers",
        "Building Core Skills",
        "Developing Daily Practices",
        "Mastering Your Mindset",
        "Creating Lasting Change",
        "Relationships and Connection",
        "Finding Your Purpose",
        "Achieving Your Goals",
        "Living Your Best Life",
      ],
      "Technology & Programming": [
        "Introduction and Setup",
        "Core Concepts and Fundamentals",
        "Essential Tools and Technologies",
        "Building Your First Project",
        "Intermediate Techniques",
        "Advanced Patterns and Practices",
        "Testing and Debugging",
        "Deployment and Production",
        "Performance Optimization",
        "Best Practices and Beyond",
      ],
      "Finance & Investment": [
        "Financial Fundamentals",
        "Understanding the Markets",
        "Building Your Portfolio",
        "Risk Management Strategies",
        "Investment Analysis",
        "Advanced Trading Techniques",
        "Tax Optimization",
        "Retirement Planning",
        "Wealth Preservation",
        "Creating Financial Freedom",
      ],
      default: [
        "Getting Started",
        "Foundation and Basics",
        "Core Concepts",
        "Practical Application",
        "Advanced Strategies",
        "Common Challenges",
        "Expert Techniques",
        "Real-World Examples",
        "Taking It Further",
        "Your Success Path",
      ],
    };

    // Get templates for the genre or use default
    let templates = genericTemplates.default;
    for (const [key, value] of Object.entries(genericTemplates)) {
      if (genre && genre.includes(key.split(" & ")[0])) {
        templates = value;
        break;
      }
    }

    // Generate titles incorporating the topic
    const titles = [];
    for (let i = 0; i < numChapters; i++) {
      if (i < templates.length) {
        // Customize template with topic if possible
        let title = templates[i];
        if (topic && i === 0) {
          title = `Introduction to ${topic}`;
        } else if (topic && i === numChapters - 1) {
          title = `Mastering ${topic}: Your Next Steps`;
        }
        titles.push(title);
      } else {
        // Generate additional chapters if needed
        titles.push(
          `Advanced Topics in ${topic || "Your Subject"} - Part ${i - templates.length + 1}`
        );
      }
    }

    return titles;
  };

  const handleInputChange = (field, value) => {
    // Limit chapters for free users with real-time Pro validation
    if (field === "chapters" && value > 3) {
      if (!validateProAccess()) {
        setShowUpgrade(true);
        return;
      }
    }

    if (stepError) setStepError("");
    // A chapter count the user picks replaces any capped restored count.
    if (field === "chapters") restoredChaptersRef.current = null;

    // Track form interactions for analytics
    trackFormInteraction(field, "change", value);

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpgrade = () => {
    // Track upgrade interest and begin checkout
    trackUpgradeInterest("upgrade_button_click");
    trackBeginCheckout();
    trackAddToCart();

    // Open PayPal payment link directly without return URL modification
    // The PayPal link should be configured in PayPal dashboard with proper return URLs
    window.open(PAYPAL_LINK, "_blank", "noopener,noreferrer");
  };

  // Handle glow animation for pricing cards
  const handleFreeCardClick = () => {
    setFreeCardGlow(true);
    setTimeout(() => setFreeCardGlow(false), 600);
  };

  const handleProCardClick = () => {
    setProCardGlow(true);
    setTimeout(() => setProCardGlow(false), 600);
  };

  // Open the in-app restart confirmation modal. The native window.confirm
  // dialog was replaced because it renders the ugly "localhost says" chrome
  // and cannot be styled to match the app.
  const handleRestart = () => {
    setShowRestartConfirm(true);
  };

  const performRestart = () => {
    // Clear saved progress for both guest and signed-in user
    clearProgressFromStorage(currentUser ? currentUser.uid : "guest");
    setSavedProgressData(null);
    setShowProgressRestorePrompt(false);

    // Reset all form data to initial state
    restoredChaptersRef.current = null;
    setFormData({
      topic: "",
      audience: "",
      genre: "",
      chapters: 3,
      tone: "professional",
      keywords: "",
      title: "",
      description: "",
    });

    // Reset all generated content
    setGeneratedOutline("");
    setGeneratedPrompts([]);

    // Reset step to beginning
    setCurrentStep(0);

    // Reset loading states
    setIsGeneratingOutline(false);

    // Close any open modals
    setShowUpgrade(false);
    setShowRestore(false);
    setShowLogin(false);
    setShowRestartConfirm(false);

    // Clear form fields for restore
    setUserEmail("");
    setVerificationCode("");
  };

  const generateOutline = async () => {
    setIsGeneratingOutline(true);

    // Generate AI-enhanced content - ONLY for Pro users
    let aiChapterTitles = [];
    let aiDescription = formData.description;
    let aiKeywords = formData.keywords;

    // Debug logging
    logger.log("=== OUTLINE GENERATION DEBUG ===");
    const hasValidProAccess = validateProAccess();
    logger.log("effectiveIsPro:", effectiveIsPro);
    logger.log("validateProAccess():", hasValidProAccess);
    logger.log("Form data:", formData);

    // Check if user has valid Pro access before making AI calls
    if (hasValidProAccess) {
      try {
        logger.log(
          "✅ Pro user confirmed - starting Firebase AI premium generation with Gemini"
        );

        const idToken = await currentUser.getIdToken();
        const authHeaders = {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`,
        };

        // Prepare request payload
        const chapterRequestPayload = {
          topic: formData.topic,
          audience: formData.audience,
          genre: formData.genre,
          title: formData.title || formData.topic,
          chapters: formData.chapters,
        };

        logger.log(
          "📤 Sending chapter generation request:",
          chapterRequestPayload
        );

        // Generate AI chapter titles for Pro users
        const chapterResponse = await fetch(
          "/.netlify/functions/generate-chapters",
          {
            method: "POST",
            headers: authHeaders,
            body: JSON.stringify(chapterRequestPayload),
          }
        );

        logger.log("🔥 Chapter API response status:", chapterResponse.status);
        logger.log(
          "🔥 Chapter API response headers:",
          Object.fromEntries(chapterResponse.headers.entries())
        );

        if (chapterResponse.ok) {
          const chapterData = await chapterResponse.json();
          logger.log("✅ Chapter generation SUCCESS:", chapterData);

          if (
            chapterData.success &&
            chapterData.chapters &&
            Array.isArray(chapterData.chapters)
          ) {
            aiChapterTitles = chapterData.chapters;
            logger.log("🎯 AI Chapter titles received:", aiChapterTitles);
          } else {
            logger.error("Invalid chapter data format:", chapterData);
            // Force fallback
            aiChapterTitles = [];
          }
        } else {
          const errorText = await chapterResponse.text();
          logger.error(
            "Chapter generation HTTP error:",
            chapterResponse.status,
            errorText
          );
          // Force fallback
          aiChapterTitles = [];
        }

        // Generate AI-enhanced description if user provided one
        if (formData.description && formData.description.trim()) {
          logger.log("Generating enhanced description...");
          const descResponse = await fetch(
            "/.netlify/functions/enhance-description",
            {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({
                topic: formData.topic,
                audience: formData.audience,
                genre: formData.genre,
                title: formData.title || formData.topic,
                userDescription: formData.description,
              }),
            }
          );

          if (descResponse.ok) {
            const descData = await descResponse.json();
            if (descData.success && descData.description) {
              aiDescription = descData.description;
              logger.log("✅ Enhanced description received");
            }
          } else {
            logger.error(
              "Description enhancement failed:",
              descResponse.status
            );
          }
        }

        // Generate AI keywords for Pro users
        logger.log("Generating AI keywords...");
        const keywordResponse = await fetch(
          "/.netlify/functions/generate-keywords",
          {
            method: "POST",
            headers: authHeaders,
            body: JSON.stringify({
              topic: formData.topic,
              audience: formData.audience,
              genre: formData.genre,
              title: formData.title || formData.topic,
            }),
          }
        );

        if (keywordResponse.ok) {
          const keywordData = await keywordResponse.json();
          if (keywordData.success && keywordData.keywords) {
            aiKeywords = keywordData.keywords;
            logger.log("✅ AI keywords received");
          }
        } else {
          logger.error("Keyword generation failed:", keywordResponse.status);
        }
      } catch (error) {
        logger.error("💥 CRITICAL AI generation error:", error);
        logger.error("Error stack:", error.stack);
        // Force fallback
        aiChapterTitles = [];
      }
    } else if (currentUser) {
      // Logged-in free user: use Gemini for AI chapter titles (capped at 3)
      logger.log("🆓 Free logged-in user - using Gemini for AI chapter titles");
      try {
        const idToken = await currentUser.getIdToken();
        const freeChapterResponse = await fetch(
          "/.netlify/functions/generate-chapters-free",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              topic: formData.topic,
              audience: formData.audience,
              genre: formData.genre,
              title: formData.title || formData.topic,
              chapters: Math.min(formData.chapters, 3),
            }),
          }
        );

        if (freeChapterResponse.ok) {
          const freeChapterData = await freeChapterResponse.json();
          if (
            freeChapterData.success &&
            Array.isArray(freeChapterData.chapters)
          ) {
            aiChapterTitles = freeChapterData.chapters;
            logger.log("✅ Free AI chapter titles received:", aiChapterTitles);
          }
        } else {
          logger.error(
            "Free chapter generation HTTP error:",
            freeChapterResponse.status
          );
        }
      } catch (err) {
        logger.error("Free chapter generation failed:", err.message);
        // Falls through to static fallback below
      }
    } else {
      logger.log("🚫 Not logged in - using static fallback chapter titles");
    }

    // Determine final chapter titles
    let selectedTopics;

    if (aiChapterTitles.length > 0) {
      logger.log("🎉 Using AI-generated chapter titles:", aiChapterTitles);
      selectedTopics = aiChapterTitles.slice(0, formData.chapters);
    } else {
      logger.log("⚠️ Falling back to smart chapter titles");
      // Generate better fallback chapter titles based on the topic and genre
      selectedTopics = generateFallbackChapterTitles(
        formData.topic,
        formData.genre,
        formData.chapters
      );
    }

    // Rewrite hype-heavy titles when the user's own input didn't justify them.
    // Includes the title field so an explicit "six figures" or "millionaire"
    // in the title doesn't get softened in the chapter list.
    selectedTopics = sanitizeChapterTitles(
      selectedTopics,
      `${formData.title} ${formData.topic} ${formData.audience} ${formData.description}`
    );

    logger.log("📋 Final selected topics:", selectedTopics);
    logger.log("=== END OUTLINE GENERATION DEBUG ===");

    // Build a topic-aware "sensitive content" hint so the AI knows when the
    // disclaimer placeholder is required (KDP and ad networks flag these
    // categories more aggressively).
    const sensitiveTopicAreas = [
      "money",
      "income",
      "invest",
      "stock",
      "crypto",
      "trading",
      "finance",
      "tax",
      "legal",
      "medical",
      "health",
      "diet",
      "weight",
      "supplement",
      "therapy",
      "psychological",
      "safety",
    ];
    const lowerTopic =
      `${formData.topic || ""} ${formData.audience || ""} ${formData.genre || ""}`.toLowerCase();
    const isSensitiveTopic = sensitiveTopicAreas.some((needle) =>
      lowerTopic.includes(needle)
    );
    const sensitiveLine = isSensitiveTopic
      ? "This book touches money, health, legal, financial, or safety topics. A clear disclaimer placeholder IS required at the start and conclusion. Phrase every outcome conservatively and never imply guaranteed results."
      : "If any claim could be interpreted as financial, medical, legal, or safety advice, add a short disclaimer placeholder.";

    // Strip markdown syntax from description for clean plain-text display.
    // The AI often returns descriptions with **bold**, # headings, and *italic* markers.
    const stripMarkdown = (text) => {
      if (!text) return text;
      return text
        .replace(/^#{1,6}\s+/gm, "")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/`(.+?)`/g, "$1")
        .trim();
    };
    const bookTitle = formData.title || formData.topic;
    const cleanDescription = (() => {
      const stripped = stripMarkdown(aiDescription);
      if (!stripped) return stripped;
      // Remove a leading line that just repeats the book title (artifact of # heading stripping)
      const lines = stripped.split("\n");
      if (lines[0].trim().toLowerCase() === bookTitle.trim().toLowerCase()) {
        return lines.slice(1).join("\n").trimStart();
      }
      return stripped;
    })();

    // Create the complete book writing prompt for final plan.
    const completeBookPrompt = `You are an experienced nonfiction ebook ghostwriter and developmental editor. Write a complete, original, KDP-friendly ebook manuscript from the brief below.

Book details:
- Title: ${bookTitle}
- Topic: ${formData.topic}
- Genre: ${formData.genre}
- Target audience: ${formData.audience}
- Tone: ${formData.tone}
- Chapters: ${formData.chapters}
- Target length: about ${formData.chapters * 3000}-${
      formData.chapters * 4000
    } words total

Description to fulfill:
${cleanDescription || "Use the topic and audience details above to frame the book clearly."}

Keywords to include naturally, only where relevant:
${aiKeywords || "No additional keywords provided."}

Chapter list:
${selectedTopics
  .slice(0, formData.chapters)
  .map((chapter, index) => `Chapter ${index + 1}: ${chapter}`)
  .join("\n")}

Compliance and integrity:
- ${sensitiveLine}
- Avoid "get rich quick", "guaranteed", "overnight", "anyone can", "passive money while you sleep", and similar hype framing.
- Do NOT invent citations, statistics, credentials, testimonials, case studies, expert quotes, or personal experiences. Where evidence or a story would help, write a clearly labeled placeholder such as [AUTHOR: insert your own example here] or [AUTHOR: verify with a current, reputable source].
- Use practical examples and hypothetical scenarios (clearly labeled "for example" or "hypothetical scenario") instead of fabricated case studies. Save real personal stories for the author to add.
- Use original phrasing. Do not copy copyrighted text, song lyrics, published passages, or recognizable proprietary material.

Output requirements:
1. Write manuscript text, not an outline and not planning notes.
2. Include in order: title page, copyright placeholder, disclaimer placeholder (required for sensitive topics, optional otherwise), table of contents, introduction, all chapters, conclusion, and a short "Author Review Checklist".
3. Start each chapter with a useful hook (a question, a concrete situation, or a clear stake) and avoid repeating the same opening pattern across chapters.
4. Use clear subheadings, short paragraphs, and reader-focused explanations. End each chapter with concise key takeaways and 3-5 action steps.
5. Be factually cautious. If a claim may be medical, legal, financial, safety-related, or unusually specific, phrase it conservatively and add a note that the author must verify it with a qualified source before publishing.
6. Avoid guaranteed outcomes, misleading income claims, keyword stuffing, filler, repetition, and generic AI-sounding phrasing ("In today's fast-paced world…", "It is important to note that…", etc.).
7. Keep the voice ${formData.tone}, useful, and appropriate for ${formData.audience}.
8. Make the manuscript easy for the author to edit after generation.

Long-form generation tips:
- A ${formData.chapters}-chapter manuscript can exceed many AI tools' single-response limits.
- If the response is cut off, type "continue" and the AI will resume from where it stopped.
- If you prefer cleaner output, generate one chapter at a time. Paste the same brief above and add: "Write Chapter N (title: <chapter title>) only, following the same rules. About 3000-4000 words. Stay consistent with previously written chapters." Repeat for each chapter, then assemble the manuscript.

Begin with the title page and continue through the full manuscript. After the conclusion, add an "Author Review Checklist" reminding the author to verify facts, edit for originality and voice, review Amazon KDP content and metadata policies, replace all placeholders with real evidence or personal stories, and confirm no fabricated citations or testimonials remain.`;

    const sep = "=".repeat(68);
    const outline = `${bookTitle}
${sep}
Generated by AI Ebook Planner  |  ${effectiveIsPro ? "Pro" : "Free"} Plan  |  ${formData.chapters} Chapters


${sep}
COMPLETE BOOK WRITING PROMPT
${sep}
Copy and paste EVERYTHING between the lines below into ChatGPT, Claude,
or your preferred AI writing tool, then press Send.

- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
${completeBookPrompt}
- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -


${sep}
BOOK OVERVIEW
${sep}
Title:           ${bookTitle}
Topic:           ${formData.topic}
Target Audience: ${formData.audience}
Genre:           ${formData.genre}
Tone:            ${formData.tone}
Chapters:        ${formData.chapters}


Book Description:
${cleanDescription}


${sep}
CHAPTER STRUCTURE
${sep}
${selectedTopics
  .slice(0, formData.chapters)
  .map((chapter, index) => `  Chapter ${index + 1}:  ${chapter}`)
  .join("\n")}


${sep}
MARKETING KEYWORDS
${sep}
${aiKeywords}


${sep}
WRITING STYLE GUIDE
${sep}
  Tone:       ${formData.tone}
  Word Count: 3,000-5,000 words per chapter
  Structure:  Hook, Main Content, Key Takeaways, 3-5 Action Steps
  Voice:      Active voice, clear, specific. Avoid filler and AI clichés.
  Elements:   Practical examples, clearly labeled hypothetical scenarios,
              and [AUTHOR: ...] placeholders for real stories and evidence.


${sep}
PRICING AND EARNINGS NOTES
${sep}
  AI drafting time:  ${formData.chapters}-${formData.chapters * 2} hours of focused work, plus editing.
  KDP price range:   $${
    formData.chapters <= 5
      ? "2.99-4.99"
      : formData.chapters <= 10
        ? "4.99-7.99"
        : "7.99-12.99"
  } (guide only, set your price based on niche, comparable titles, length)

  Note: Income from a self-published ebook is not guaranteed. It depends
  on niche demand, book quality, cover, description, reviews, pricing,
  marketing, and reader interest. Consistent marketing builds sales.


${sep}
AUTHOR CHECKLIST, BEFORE YOU PUBLISH
${sep}
  [ ] Replace every [AUTHOR: ...] placeholder with your real examples
      and verified evidence.
  [ ] Remove or rewrite any "get rich quick", guaranteed, or overnight claim.
  [ ] Fact-check every number, study, quote, or credential, cite a real
      source or remove the claim.
  [ ] Keep the disclaimer placeholder if the book covers money, health,
      legal, financial, or safety topics.
  [ ] Run a plagiarism check on the full manuscript.
  [ ] Review Amazon KDP content guidelines for title, description, and
      keywords compliance.
  [ ] Edit for voice and originality, add your own perspective so the
      book sounds like you, not a generic AI draft.
  [ ] Proofread cover, title page, copyright, table of contents,
      and back matter.
`;

    setGeneratedOutline(outline);
    setIsGeneratingOutline(false);

    // Track successful ebook generation
    trackEbookGeneration(
      hasValidProAccess ? "pro" : "free",
      formData.genre || "general",
      formData.chapters
    );
  };

  const renderPromptText = (text) => {
    const renderInline = (str) => {
      const parts = str.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, j) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={j}>{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      );
    };
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} className="h-1.5" />;
      if (/^#{1,3} /.test(line)) {
        return (
          <div key={i} className="font-semibold mt-2 mb-0.5">
            {renderInline(line.replace(/^#+\s/, ""))}
          </div>
        );
      }
      if (/^[-*] /.test(line)) {
        return (
          <div key={i} className="flex gap-1.5 pl-2">
            <span className="shrink-0 mt-px">•</span>
            <span>{renderInline(line.slice(2))}</span>
          </div>
        );
      }
      if (/^\d+\. /.test(line)) {
        const num = line.match(/^(\d+)\./)[1];
        return (
          <div key={i} className="flex gap-1.5 pl-2">
            <span className="shrink-0 mt-px">{num}.</span>
            <span>{renderInline(line.replace(/^\d+\.\s*/, ""))}</span>
          </div>
        );
      }
      return <div key={i}>{renderInline(line)}</div>;
    });
  };

  const generatePrompts = () => {
    const bookTitle = formData.title || formData.topic;

    // Free users get 2 solid, KDP-compliant prompts
    const basicPrompts = [
      {
        title: "📝 Chapter Writer",
        prompt: `You are a professional nonfiction writer. Write a complete chapter (2,500-3,500 words) for an ebook on ${formData.topic}.

CHAPTER TO WRITE: [Replace with your chapter title]

BOOK DETAILS:
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Tone: ${formData.tone}

STRUCTURE:
1. Opening hook, use a question, concrete scenario, or clear stake (not "In this chapter, we will...")
2. 3-5 main sections with clear subheadings and short paragraphs
3. Practical examples or clearly labeled hypothetical scenarios
4. [AUTHOR: insert your own story or example here] wherever a personal anecdote would help
5. Key Takeaways (4-6 bullet points)
6. Action Steps (3-5 specific tasks the reader can do right away)

COMPLIANCE:
- Do not invent statistics, quotes, or case studies. Add [AUTHOR: verify with a reputable source] for any specific data.
- Avoid "guaranteed", "overnight", or exaggerated outcome language.
- If you reference money, health, legal, or safety topics, phrase claims conservatively.

Start directly with the chapter hook. Do not write an outline, write the full chapter text.`,
      },
      {
        title: "📣 Amazon Book Description Writer",
        prompt: `You are a copywriting specialist for self-published nonfiction. Write an Amazon KDP book description for "${bookTitle}".

BOOK DETAILS:
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Tone: ${formData.tone}
- Keywords to weave in naturally: ${formData.keywords}

REQUIREMENTS:
1. Length: 200-400 words
2. Open with a hook that speaks directly to the reader's main frustration or goal
3. Use short paragraphs and 3-5 benefit-focused bullet points
4. Phrase every benefit honestly, no guaranteed results, no hype, no exaggerated claims
5. End with a clear, low-pressure call to action
6. Include keywords naturally, no stuffing

You may use basic HTML (<b>, <br>) where it improves readability in the KDP editor.

Write the description now.`,
      },
    ];

    // Pro users get significantly enhanced, professional-grade prompts
    const proPrompts = [
      {
        title: "🚀 Complete Book Writer, Full Manuscript (Pro)",
        prompt: `You are an experienced nonfiction ghostwriter and developmental editor. Write a complete, original, KDP-compliant manuscript for "${bookTitle}".

BOOK DETAILS:
- Title: ${bookTitle}
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Tone: ${formData.tone}
- Chapters: ${formData.chapters}
- Target length: ${formData.chapters * 4000} words

MANUSCRIPT STRUCTURE (write all in order):
1. Title page
2. Copyright placeholder
3. Disclaimer placeholder (required, this topic touches financial/practical areas)
4. Table of contents
5. Introduction: compelling hook, honest reader promise, chapter roadmap
6. All ${formData.chapters} chapters, each with:
   - Opening hook (vary the approach each chapter, question, scenario, clear stake)
   - 3-5 sections with clear subheadings and short paragraphs
   - Practical examples or clearly labeled hypothetical scenarios
   - [AUTHOR: insert your own story or example here] wherever firsthand experience fits
   - Key Takeaways (4-6 bullet points)
   - 3-5 Action Steps the reader can act on immediately
7. Conclusion
8. Author Review Checklist

COMPLIANCE RULES, follow strictly:
- Do NOT invent statistics, quotes, case studies, expert citations, or testimonials. Add [AUTHOR: verify with a reputable source] wherever specific data would help.
- Replace [AUTHOR: insert your own story or example] wherever a personal anecdote would strengthen the point.
- Do NOT use "guaranteed", "overnight results", or similar hype language. Phrase every outcome conservatively.
- If this topic touches money, health, legal, or safety: add a clear disclaimer at the start and conclusion.

WRITING STYLE:
- ${formData.tone} tone throughout
- Active voice, specific language, short paragraphs
- No filler phrases ("In today's fast-paced world...", "It is important to note...", "Needless to say...")
- Vary chapter openings so they don't all start the same way

Write the full manuscript now, starting with the title page. If the response is cut off, type "continue" to resume.`,
      },
      {
        title: "🎯 Advanced Marketing Angles Generator (Pro)",
        prompt: `You are a marketing strategist specializing in book launches. Generate 7 unique marketing angles for the ebook "${
          formData.title || formData.topic
        }" about ${formData.topic}.

TARGET AUDIENCE: ${formData.audience}
GENRE: ${formData.genre}
KEYWORDS: ${formData.keywords}

For each marketing angle, provide:

**ANGLE 1: Problem-Solution Positioning**
- Hook: [Specific pain point your audience faces]
- Solution: How your book solves it uniquely
- Proof: [Testimonial or result you could highlight]
- CTA: Compelling call-to-action

**ANGLE 2: Authority/Expert Positioning**
- Credibility: [Your unique background/experience to highlight]
- Exclusive insights: What others don't teach
- Social proof: [Type of endorsements to seek]

**ANGLE 3: Transformation Story**
- Before state: Where readers are now
- After state: Where they'll be after reading
- Journey: The transformation process
- Timeline: How quickly they'll see results

**ANGLE 4: Contrarian/Against-the-Grain**
- Common belief: What everyone else teaches
- Your different approach: Why conventional wisdom fails
- Unique method: [Your contrarian strategy]

**ANGLE 5: Urgency/Timeliness**
- Current trend: Why this topic is hot now
- Window of opportunity: Why they need to act fast
- Future consequences: What happens if they wait

**ANGLE 6: Community/Movement**
- Tribe identity: What group they'll join
- Shared values: What unites your readers
- Exclusive access: [Special community or resources]

**ANGLE 7: Results-Focused**
- Specific outcomes: [Realistic skills or knowledge the reader will gain]
- Success metrics: How readers can track their own progress
- Confidence builder: What makes this book a low-risk, high-value investment (no guarantees, focus on the quality and specificity of the content)

For each angle, also suggest:
- Best platform to use it (Instagram, Facebook, email, etc.)
- Content format (video, carousel, story, etc.)
- Hashtags and keywords to include`,
      },
      {
        title: "📖 Bestseller Book Introduction (Pro)",
        prompt: `You are a ghostwriter for New York Times bestselling authors. Write a compelling introduction for "${
          formData.title || formData.topic
        }" that hooks readers from the first sentence.

BOOK DETAILS:
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Tone: ${formData.tone}
- Genre: ${formData.genre}

INTRODUCTION STRUCTURE (800-1,200 words):

**OPENING HOOK** (150-200 words):
Start with ONE of these proven techniques:
- Shocking statistic or little-known fact
- Personal story of transformation
- Provocative question that challenges assumptions
- Vivid scene that illustrates the problem
[CHOOSE THE BEST FIT AND INSERT YOUR SPECIFIC STORY/STAT HERE]

**THE PROBLEM** (200-300 words):
- Paint a picture of the reader's current frustration
- Use "you" language to make it personal
- Include emotional triggers and pain points
- Reference common struggles your audience faces
- [INSERT SPECIFIC EXAMPLES FROM YOUR EXPERIENCE]

**THE PROMISE** (200-300 words):
- State clearly and honestly what readers will learn and be able to do
- List 3-5 specific skills or insights they'll take away
- Explain why your approach or perspective is distinct
- Use language like "you'll learn how to..." or "by the end, you'll have a clear plan for...", avoid guaranteed results
- [AUTHOR: describe what makes your approach or framework unique]

**CREDIBILITY** (150-200 words):
- Brief, honest background on why you wrote this book and why you're positioned to teach it
- [AUTHOR: insert your relevant experience, background, or firsthand knowledge]
- Keep this grounded, no invented credentials or fabricated success stories

**ROADMAP** (200-300 words):
- Overview of what each chapter covers
- Explain the logical progression
- Build excitement for the journey ahead
- Set expectations for time investment and results
- End with an inspiring call to action

COMPLIANCE:
- Do not invent statistics, testimonials, or case studies. Add [AUTHOR: verify with a reputable source] for any specific claims.
- Phrase every benefit conservatively, no guaranteed results.
- Add [AUTHOR: insert your personal story or credentials here] wherever firsthand experience would build trust.

WRITING STYLE:
- ${formData.tone} tone throughout
- Active voice, varied sentence length, short paragraphs
- Confident and clear, earn the reader's trust through honesty, not hype
- No filler opening phrases ("In today's world...", "Now more than ever...")`,
      },
      {
        title: "💰 Revenue Maximization Strategy (Pro)",
        prompt: `You are a business strategist specializing in information product monetization. Create a comprehensive revenue strategy for the ebook "${
          formData.title || formData.topic
        }" about ${formData.topic}.

BOOK DETAILS:
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Chapter Count: ${formData.chapters}

DEVELOP A COMPLETE MONETIZATION ECOSYSTEM:

**PRICING STRATEGY:**
- Primary price point with psychological reasoning
- Launch pricing sequence (early bird → regular → premium)
- Bundle options and upsells
- Platform-specific pricing (Amazon vs. direct sales)

**PRODUCT LADDER:**
1. **Lead Magnet** (Free):
   - [Suggest specific free resource to build email list]
   - Connection to main book topic
   - Value proposition

2. **Core Product** (Your Ebook):
   - Optimal price point: $[X.XX] and reasoning
   - Key selling points and differentiators
   - Bonus materials to include

3. **Upsell Products** ($50-200):
   - Workbook or implementation guide
   - Video course or masterclass
   - 1-on-1 consultation or group coaching
   - [Suggest 2-3 specific upsell ideas]

4. **High-Ticket Offerings** ($500+):
   - Done-with-you program
   - Mastermind or group coaching
   - Speaking or consulting services

**REVENUE STREAMS:**
- Direct sales revenue projections
- Affiliate marketing opportunities
- Speaking engagement potential
- Course creation possibilities
- Coaching/consulting spin-offs
- [List 3-5 additional revenue streams specific to your topic]

**LAUNCH SEQUENCE:**
- Pre-launch (build anticipation)
- Launch week strategy
- Post-launch momentum maintenance
- Long-term evergreen sales system

**MARKETING CHANNELS:**
- Primary channel recommendation with reasoning
- Secondary channels for diversification
- Content marketing strategy
- Partnership opportunities
- [Specific to ${formData.audience} preferences]

Present all revenue estimates as illustrative ranges rather than guaranteed figures, actual results depend on niche demand, book quality, marketing effort, and many factors outside the author's control. For each revenue stream, provide a realistic range, key variables that influence it, and 3 specific next steps.`,
      },
      {
        title: "🎨 Multi-Platform Book Descriptions (Pro)",
        prompt: `You are a copywriting expert specializing in book marketing. Create optimized book descriptions for multiple platforms for "${
          formData.title || formData.topic
        }".

BOOK DETAILS:
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Tone: ${formData.tone}
- Keywords: ${formData.keywords}

CREATE DESCRIPTIONS FOR:

**1. AMAZON KDP DESCRIPTION** (300-400 words):
- SEO-optimized with keywords: ${formData.keywords}
- Benefit-focused bullet points
- Social proof placeholders
- Strong call-to-action
- HTML formatting tags included
- "Look inside" hook for first paragraph

**2. SOCIAL MEDIA DESCRIPTION** (150-200 words):
- Platform: Instagram/Facebook
- Emoji usage for visual appeal
- Hashtag suggestions (#BookLovers #${formData.genre.replace(/\s+/g, "")} etc.)
- Shareable and engaging tone
- Clear value proposition

**3. EMAIL MARKETING DESCRIPTION** (200-250 words):
- Subject line suggestions (5 options)
- Personal, conversational tone
- Urgency and scarcity elements
- Exclusive subscriber benefits
- Multiple CTA options

**4. PRESS RELEASE DESCRIPTION** (250-300 words):
- Professional, newsworthy angle
- Industry statistics and trends
- Author credentials highlight
- Media hook and story angle
- Contact information template

**5. BACK COVER COPY** (200-250 words):
- Compelling headline
- Problem/solution format
- Reader testimonial placeholder
- Author bio snippet
- ISBN and pricing area

**6. GOODREADS DESCRIPTION** (300-350 words):
- Reader-focused language
- Genre-specific elements
- Discussion-worthy hooks
- Reading group appeal
- Star rating optimization tips

For each description, include:
- Primary emotional trigger
- Key benefit highlighted
- Target keyword density
- Conversion optimization tips
- A/B testing suggestions

Make each description feel native to its platform while maintaining consistent messaging about the book's core value.`,
      },
      {
        title: "🧠 Reader Psychology & Engagement Hooks (Pro)",
        prompt: `You are a behavioral psychologist and bestselling author coach. Create advanced reader engagement strategies for "${
          formData.title || formData.topic
        }" targeting ${formData.audience}.

PSYCHOLOGICAL ENGAGEMENT FRAMEWORK:

**1. ATTENTION GRABBERS** (Chapter Openings):
Create 5 different hook formulas and write one example of each for a chapter in this book:
- The Concrete Scenario Hook: drop the reader into a recognizable situation
- The Reframing Question: challenge a common assumption the reader holds
- The Clear Stakes Hook: state plainly what's at risk if the reader doesn't act
- The Surprising Fact Hook: lead with a counterintuitive but verifiable point (add [AUTHOR: verify before publishing])
- The Before/After Hook: contrast where the reader is now vs. where they could realistically be

**2. RETENTION TECHNIQUES** (Keep Them Reading):
- Cliffhanger transitions between chapters
- "Coming up next" previews
- Internal callbacks to earlier content
- Progress indicators ("You're now 60% closer to...")
- Achievement unlocks ("You've now mastered the basics...")

**3. EMOTIONAL TRIGGERS** (${formData.tone} tone):
- Pain point amplification techniques
- Aspiration and desire building
- Fear of missing out (FOMO) creation
- Social proof and belonging needs
- Achievement and recognition desires
- [Customize for ${formData.audience} specific motivations]

**4. TRUST & CREDIBILITY BUILDERS**:
- Authority: How to demonstrate expertise through specific, verifiable knowledge rather than claims
- Social proof: How to reference real reader outcomes honestly (only what the author can actually verify, no fabricated testimonials)
- Reciprocity: Give genuine value upfront so readers trust the recommendations that follow
- Commitment: How to get readers to make small, achievable commitments that build momentum

**5. INTERACTIVE ELEMENTS**:
- Self-assessment quizzes
- "Pause and Reflect" moments
- Action challenges between chapters
- Progress tracking worksheets
- Community discussion prompts

**6. NEUROLOGICAL ENGAGEMENT**:
- Dopamine triggers (small wins and achievements)
- Pattern interrupts (format changes, surprises)
- Memory anchors (memorable phrases, acronyms)
- Visualization exercises
- Sensory language usage

**7. PERSONALIZATION STRATEGIES**:
- Reader avatar addressing ("If you're the type of person who...")
- Multiple pathway options ("Choose your approach...")
- Skill level adaptations ("Beginner/Intermediate/Advanced")
- Industry-specific examples
- [Tailor to ${formData.audience} demographics]

**8. COMPLETION PSYCHOLOGY**:
- Chapter length optimization for ${formData.audience} (ideal range and reasoning)
- How to celebrate reader progress in a way that feels genuine, not hollow
- Momentum building: how each chapter's action steps set up the next chapter naturally
- How to make the final chapters feel like a payoff, not an ending
- What to include after the conclusion to keep readers engaged with the author's work

For each strategy, provide:
- Specific implementation examples
- Psychological reasoning
- Expected reader response
- Measurement metrics
- Adaptation guidelines for different reader types

The goal is a reading experience that is genuinely useful and engaging, one that earns the reader's attention through real value, not manipulation.`,
      },
      {
        title: "📈 Advanced Author Platform Builder (Pro)",
        prompt: `You are a personal branding expert specializing in author platform development. Create a comprehensive platform-building strategy for the author of "${
          formData.title || formData.topic
        }".

AUTHOR DETAILS:
- Book Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Expertise Area: [INSERT YOUR BACKGROUND/CREDENTIALS]

BUILD A COMPLETE AUTHOR PLATFORM:

**1. PERSONAL BRAND FOUNDATION**:
- Brand positioning statement (one sentence)
- Unique value proposition
- Core message and mission
- Visual brand guidelines (colors, fonts, imagery style)
- Voice and tone guidelines
- [Differentiation from other ${formData.genre} authors]

**2. CONTENT STRATEGY** (90-day plan):
- Blog post topics (12 high-value articles)
- Social media content calendar
- Email newsletter sequence (welcome series + weekly topics)
- Podcast guest pitch topics
- Video content ideas
- [Platform-specific content for ${formData.audience}]

**3. SOCIAL MEDIA OPTIMIZATION**:
- Platform priority ranking (focus on top 2-3)
- Profile optimization for each platform
- Content pillars and themes
- Engagement strategies
- Hashtag research and strategy
- Influencer collaboration opportunities

**4. EMAIL LIST BUILDING**:
- Lead magnet ideas (5 options related to ${formData.topic})
- Opt-in page copy and design brief
- Email sequence templates
- Segmentation strategy
- Automation workflows
- List growth tactics

**5. AUTHORITY BUILDING**:
- Speaking opportunity targets
- Podcast guest strategy
- Media pitch angles
- Industry publication targets
- Award and recognition opportunities
- [Specific to ${formData.genre} industry]

**6. NETWORKING & PARTNERSHIPS**:
- Key influencers to connect with
- Collaboration opportunities
- Joint venture possibilities
- Mastermind or group participation
- Industry event attendance plan

**7. CONTENT REPURPOSING SYSTEM**:
- Book content → blog posts
- Blog posts → social media content
- Social media → email newsletters
- Email content → speaking topics
- Speaking topics → new book ideas

**8. MONETIZATION INTEGRATION**:
- How platform supports book sales
- Additional revenue stream development
- Product launch sequences
- Affiliate marketing opportunities
- Sponsored content guidelines

**9. MEASUREMENT & ANALYTICS**:
- Key performance indicators (KPIs)
- Tracking tools and systems
- Monthly review process
- Growth milestone targets
- ROI measurement methods

**10. SCALING STRATEGY**:
- Team building plan (VA, social media manager, etc.)
- System automation opportunities
- Passive income development
- Long-term vision and goals
- Exit strategy considerations

Provide specific action steps, timelines, and resource requirements for each element. Include templates, scripts, and examples wherever possible.`,
      },
      {
        title: "🎪 Launch Campaign Masterplan (Pro)",
        prompt: `You are a book launch strategist who has orchestrated multiple bestseller campaigns. Create a comprehensive 90-day launch plan for "${
          formData.title || formData.topic
        }".

BOOK DETAILS:
- Topic: ${formData.topic}
- Target Audience: ${formData.audience}
- Genre: ${formData.genre}
- Chapter Count: ${formData.chapters}
- Keywords: ${formData.keywords}

**PRE-LAUNCH PHASE** (Days -60 to -1):

**Weeks 1-2: Foundation Building**
- Author platform audit and optimization
- Email list building campaign launch
- Social media content calendar creation
- Influencer outreach list compilation
- Beta reader recruitment (target: 20-50 readers)

**Weeks 3-4: Content Creation**
- Lead magnet development and testing
- Book trailer or promotional video creation
- Podcast guest pitch development
- Media kit and press release preparation
- Review copy preparation

**Weeks 5-6: Relationship Building**
- Influencer and blogger outreach
- Podcast booking confirmations
- Joint venture partnership discussions
- Media contact cultivation
- Industry connection strengthening

**Weeks 7-8: Pre-Launch Buzz**
- Behind-the-scenes content sharing
- Sneak peek and excerpt releases
- Pre-order campaign setup
- Early bird bonus creation
- Anticipation building content

**LAUNCH WEEK** (Days 1-7):
- Day 1: Official announcement and celebration
- Day 2: Media interviews and podcast releases
- Day 3: Social media blitz and user-generated content
- Day 4: Email marketing push and affiliate activation
- Day 5: Influencer collaboration posts
- Day 6: Community engagement and Q&A sessions
- Day 7: Week recap and momentum maintenance

**POST-LAUNCH PHASE** (Days 8-90):

**Week 2-3: Momentum Maintenance**
- Review request campaigns
- Additional media opportunities
- Content marketing continuation
- Sales data analysis and optimization
- Customer feedback collection

**Week 4-8: Expansion Strategy**
- International market exploration
- Additional format development (audiobook, course)
- Speaking opportunity pursuit
- Partnership expansion
- Upsell product development

**Week 9-12: Long-term Strategy**
- Evergreen marketing system setup
- Next book planning
- Platform growth continuation
- Revenue stream diversification
- Success metric evaluation

**SPECIFIC TACTICS FOR ${formData.audience}:**
- Platform-specific strategies
- Community engagement approaches
- Content format preferences
- Timing and frequency optimization
- Messaging customization

**BUDGET ALLOCATION** (Suggested percentages):
- Advertising and promotion: 40%
- Content creation: 25%
- Tools and software: 15%
- Influencer partnerships: 10%
- Miscellaneous/contingency: 10%

**SUCCESS METRICS:**
- Sales targets by week
- Email list growth goals
- Social media engagement rates
- Media mention targets
- Review acquisition goals
- Long-term platform growth metrics

**CRISIS MANAGEMENT:**
- Negative review response strategy
- Technical issue contingency plans
- Competition response tactics
- Momentum loss recovery plans
- Pivot strategies if needed

Include specific templates, scripts, and checklists for each phase of the campaign.`,
      },
    ];

    const hasValidProAccess = validateProAccess();
    setGeneratedPrompts(hasValidProAccess ? proPrompts : basicPrompts);
  };

  const buildFullPlanText = () => {
    const promptSections = generatedPrompts
      .map((p, i) => `${i + 1}. ${p.title}\n${"=".repeat(60)}\n${p.prompt}`)
      .join("\n\n");
    return (
      generatedOutline +
      (promptSections
        ? `\n\n${"=".repeat(68)}\nAI CONTENT PROMPTS\n${"=".repeat(68)}\n\n${promptSections}`
        : "")
    );
  };

  const downloadAsPDF = () => {
    trackButtonClick("download_pdf", `plan_${effectiveIsPro ? "pro" : "free"}`);
    const title = formData.title || formData.topic || "Ebook Plan";
    const content = buildFullPlanText()
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${title}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.7; color: #1a1a1a; max-width: 780px; margin: 0 auto; padding: 32px 20px; }
  pre { white-space: pre-wrap; word-wrap: break-word; font-family: Arial, sans-serif; font-size: 11pt; }
  .print-btn { background: #6366f1; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-size: 13px; cursor: pointer; margin-bottom: 24px; }
  .hint { color: #666; font-size: 11px; margin-bottom: 24px; }
  @media print { .print-btn, .hint { display: none; } }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
  <p class="hint">In the print dialog, choose "Save as PDF" as the destination.</p>
  <pre>${content}</pre>
</body></html>`);
    printWindow.document.close();
  };

  const downloadAsWord = () => {
    trackButtonClick(
      "download_word",
      `plan_${effectiveIsPro ? "pro" : "free"}`
    );
    const title = formData.title || formData.topic || "Ebook Plan";
    const content = buildFullPlanText()
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title}</title>
<style>body { font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.7; } pre { white-space: pre-wrap; word-wrap: break-word; font-family: Arial, sans-serif; }</style>
</head><body><pre>${content}</pre></body></html>`;
    const blob = new Blob(["﻿", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-ebook-plan.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const validateStep = () => {
    if (currentStep === 0) {
      if (!formData.topic.trim())
        return "Please enter your ebook topic before continuing.";
      if (!formData.genre) return "Please select a genre before continuing.";
    }
    if (currentStep === 1) {
      if (!formData.audience.trim())
        return "Please describe your target audience before continuing.";
    }
    return "";
  };

  const nextStep = () => {
    const error = validateStep();
    if (error) {
      setStepError(error);
      return;
    }
    setStepError("");
    if (currentStep === 2) {
      generateOutline();
      generatePrompts(); // re-sync prompts with current pro status before showing step 3
    }
    if (currentStep === 1) {
      generatePrompts();
    }
    if (currentStep < steps.length - 1) {
      const nextStepNumber = currentStep + 1;
      setCurrentStep(nextStepNumber);

      // Track form progression
      if (nextStepNumber === steps.length - 1) {
        trackFormComplete(formData);
      } else {
        trackFormStart(nextStepNumber);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Runs twice (phone and wider layouts), so field ids get a layout prefix.
  const renderStep = (idPrefix) => {
    const fieldId = (name) => `${idPrefix}-${name}`;
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label
                htmlFor={fieldId("topic")}
                className="block text-sm font-medium text-white mb-2"
              >
                What's your ebook topic? (Be specific for better AI results)
              </label>
              <input
                id={fieldId("topic")}
                type="text"
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm placeholder-gray-300 transition-all duration-300 hover:bg-white/15"
                placeholder="e.g., 'Personal Finance for Remote Workers', 'Mediterranean Diet for Busy Moms', 'Python Programming for Data Science'"
                value={formData.topic}
                onChange={(e) => handleInputChange("topic", e.target.value)}
                onFocus={() => trackFormStart(0)}
              />
            </div>

            <div>
              <label
                htmlFor={fieldId("genre")}
                className="block text-sm font-medium text-white mb-2"
              >
                Choose your genre (This helps AI match the right writing style)
              </label>
              <select
                id={fieldId("genre")}
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/15 [&>option]:text-gray-800 [&>option]:bg-white"
                value={formData.genre}
                onChange={(e) => handleInputChange("genre", e.target.value)}
              >
                <option value="" className="text-gray-800 bg-white">
                  Select a genre...
                </option>
                {genres.map((genre) => (
                  <option
                    key={genre}
                    value={genre}
                    className="text-gray-800 bg-white"
                  >
                    {genre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor={fieldId("title")}
                className="block text-sm font-medium text-white mb-2"
              >
                Book title (A strong title helps AI create focused content)
              </label>
              <input
                id={fieldId("title")}
                type="text"
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm placeholder-gray-300 transition-all duration-300 hover:bg-white/15"
                placeholder="e.g., 'The 30-Day Money Makeover', 'Code Your Way to Success', 'Mindful Parenting in the Digital Age'"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label
                htmlFor={fieldId("audience")}
                className="block text-sm font-medium text-white mb-2"
              >
                Who is your ideal reader? (Demographics, needs, goals)
              </label>
              <textarea
                id={fieldId("audience")}
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent h-24 sm:h-32 text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm placeholder-gray-300 transition-all duration-300 hover:bg-white/15"
                placeholder="e.g., 'Busy parents aged 30-45 looking for quick healthy meal solutions', 'College students learning programming who need practical projects', 'Entrepreneurs starting their first online business'"
                value={formData.audience}
                onChange={(e) => handleInputChange("audience", e.target.value)}
              />
            </div>

            <div>
              <label
                htmlFor={fieldId("tone")}
                className="block text-sm font-medium text-white mb-2"
              >
                How should your book sound? (This sets the AI's writing voice)
              </label>
              <select
                id={fieldId("tone")}
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/15 [&>option]:text-gray-800 [&>option]:bg-white"
                value={formData.tone}
                onChange={(e) => handleInputChange("tone", e.target.value)}
              >
                {tones.map((tone) => (
                  <option
                    key={tone.value}
                    value={tone.value}
                    className="text-gray-800 bg-white"
                  >
                    {tone.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor={fieldId("keywords")}
                className="block text-sm font-medium text-white mb-2"
              >
                SEO keywords for Amazon/marketing (AI will naturally include
                these)
              </label>
              <input
                id={fieldId("keywords")}
                type="text"
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm placeholder-gray-300 transition-all duration-300 hover:bg-white/15"
                placeholder="e.g., 'passive income, financial freedom, debt free, investment strategies, wealth building'"
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
              />
            </div>
          </div>
        );

      case 2: {
        const maxChapters = effectiveIsPro ? 15 : 3;
        return (
          <div className="space-y-6">
            {!currentUser ? (
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Sparkles size={22} className="text-blue-600 shrink-0" />
                  <div>
                    <p className="text-blue-900 font-semibold text-sm">
                      Sign in to get AI-generated chapter titles
                    </p>
                    <p className="text-blue-700 text-xs mt-0.5">
                      Free and Pro accounts both get AI-generated chapter
                      titles. Sign in to unlock this feature.
                    </p>
                  </div>
                </div>
                <button
                  onClick={openLoginModal}
                  className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                >
                  Sign In
                </button>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
                <Sparkles size={18} className="text-green-600 shrink-0" />
                <p className="text-green-800 text-sm font-medium">
                  {effectiveIsPro
                    ? "AI-generated chapter titles are included with your Pro plan."
                    : "AI-generated chapter titles are included with your free account."}
                </p>
              </div>
            )}

            {!effectiveIsPro && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={20} className="text-yellow-800" />
                  <span className="text-yellow-800 font-medium">
                    Free Version Limitations
                  </span>
                </div>
                <p className="text-yellow-700 text-sm mb-3">
                  You're using the free version (3-chapter limit, basic prompts
                  only). Upgrade to Pro for up to 15 chapters and advanced AI
                  prompts!
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpgrade}
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl transition-all text-sm font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105"
                  >
                    🚀 Upgrade to Pro - $19
                  </button>
                  <button
                    onClick={() => {
                      // Clear any pre-filled data for security
                      openRestoreModal({ clearFields: true });
                    }}
                    className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-all text-sm font-medium border border-white/10"
                  >
                    <RefreshCw size={16} className="inline mr-1" />
                    Activate Pro Access
                  </button>
                </div>
              </div>
            )}

            {effectiveIsPro && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Unlock size={20} className="text-green-800" />
                      <span className="text-green-800 font-medium">
                        PRO VERSION ACTIVE
                      </span>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      Up to 15 chapters, advanced AI prompts unlocked!
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-green-800">
                      <Calendar size={16} />
                      <span className="text-sm font-medium">
                        {effectiveIsLifetimePro
                          ? "Lifetime access"
                          : `${effectiveDaysRemaining} days left`}
                      </span>
                    </div>
                    {!effectiveIsLifetimePro && effectiveDaysRemaining <= 5 && (
                      <button
                        onClick={handleUpgrade}
                        className="text-xs text-green-600 hover:underline mt-1"
                      >
                        Renew now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor={fieldId("chapters")}
                className="block text-sm font-medium text-white mb-2"
              >
                Number of chapters: {formData.chapters}{" "}
                {!effectiveIsPro &&
                  formData.chapters >= 3 &&
                  "(Maximum for free version)"}
              </label>
              <input
                id={fieldId("chapters")}
                type="range"
                min="3"
                max={maxChapters}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                value={formData.chapters}
                onChange={(e) =>
                  handleInputChange("chapters", parseInt(e.target.value))
                }
              />
              <div className="flex justify-between text-xs text-gray-300 mt-2">
                <span>3 chapters</span>
                <span>{maxChapters} chapters</span>
              </div>
            </div>

            <div>
              <label
                htmlFor={fieldId("description")}
                className="block text-sm font-medium text-white mb-2"
              >
                Book description/back cover copy
              </label>
              <textarea
                id={fieldId("description")}
                className="w-full p-3 sm:p-4 border border-white/20 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent h-24 sm:h-32 text-white text-base min-h-[44px] bg-white/10 backdrop-blur-sm placeholder-gray-300 transition-all duration-300 hover:bg-white/15"
                placeholder="Describe what your book is about, what readers will learn, and the main benefits they'll get..."
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
              />
            </div>
          </div>
        );
      }

      case 3:
        return (
          <div className="space-y-6">
            {!currentUser ? (
              <div className="bg-blue-50 border border-blue-300 rounded-xl p-6 text-center space-y-3">
                <div className="text-3xl">🔒</div>
                <h3 className="font-semibold text-blue-900 text-base">
                  Sign in to access AI writing prompts
                </h3>
                <p className="text-blue-800 text-sm max-w-md mx-auto">
                  Free accounts get 2 ready-to-use writing prompts. Pro accounts
                  unlock 8 advanced prompts including a full manuscript writer,
                  marketing angles, launch plan, and more.
                </p>
                <button
                  onClick={openLoginModal}
                  className="mt-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
                >
                  Sign In to Get Your Prompts
                </button>
              </div>
            ) : (
              <>
                <div className="bg-blue-100 p-4 rounded-lg border border-blue-300">
                  <h3 className="font-medium text-blue-900 mb-2">
                    AI Content Generation Prompts
                  </h3>
                  <p className="text-blue-800 text-sm">
                    {effectiveIsPro
                      ? "Copy these advanced prompts to use with ChatGPT, Claude, or your preferred AI writing tool."
                      : "Copy these prompts into ChatGPT, Claude, or any AI writing tool to start writing your book."}
                  </p>
                  {generatedPrompts.length > 2 ? (
                    <span className="inline-block mt-2 bg-blue-200 text-blue-800 text-xs px-2 py-1 rounded-full">
                      PRO: {generatedPrompts.length} Advanced Prompts Available
                    </span>
                  ) : (
                    <span className="inline-block mt-2 bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded-full">
                      FREE: {generatedPrompts.length} Writing Prompts
                    </span>
                  )}
                </div>

                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {generatedPrompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="border border-gray-300 rounded-lg p-4 bg-gray-100"
                    >
                      <h4 className="font-medium text-gray-900 mb-2">
                        {prompt.title}
                        {effectiveIsPro && index >= 3 && (
                          <span className="ml-2 bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full">
                            PRO ONLY
                          </span>
                        )}
                      </h4>
                      <div className="bg-white p-3 rounded text-sm text-gray-800 max-h-40 overflow-y-auto border border-gray-200 leading-relaxed">
                        {renderPromptText(prompt.prompt)}
                      </div>
                      <button
                        onClick={() => {
                          trackDemoInteraction("copy_prompt", prompt.title);
                          navigator.clipboard.writeText(prompt.prompt);
                        }}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800 px-3 py-2 min-h-[44px] flex items-center justify-center bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        📋 Copy prompt
                      </button>
                    </div>
                  ))}
                </div>

                {!effectiveIsPro && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 text-sm">
                      <AlertCircle size={16} className="inline mr-1" />
                      Upgrade to Pro for 8 advanced prompts: full manuscript
                      writer, marketing angles, launch plan, revenue strategy,
                      and more.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="bg-green-100 p-4 rounded-lg border border-green-300">
              <h3 className="font-medium text-green-900 mb-2">
                🎉 Your Complete Book-Writing Prompt is Ready!
              </h3>
              <p className="text-green-800 text-sm">
                Copy the prompt below and paste it into any AI (ChatGPT, Claude,
                Gemini) to instantly generate your complete ebook!
                {effectiveIsPro
                  ? " (Pro version - Full book generation with advanced AI)"
                  : " (Free version - Basic chapter generation)"}
              </p>
            </div>

            <div>
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedOutline).then(() => {
                      setCopiedAll(true);
                      setTimeout(() => setCopiedAll(false), 2500);
                    });
                  }}
                  className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow ${
                    copiedAll
                      ? "bg-gray-400 text-white cursor-default"
                      : "bg-purple-600 hover:bg-purple-700 text-white"
                  }`}
                >
                  {copiedAll ? "✅ Copied!" : "📋 Copy All"}
                </button>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg max-h-96 overflow-y-auto border border-gray-300">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                  {generatedOutline}
                </pre>
              </div>
            </div>

            <div className="bg-blue-100 p-4 rounded-lg border border-blue-300">
              <h3 className="font-medium text-blue-900 mb-3">
                🚀 Your Complete Action Plan:
              </h3>
              <div className="space-y-3">
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      1
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Copy Your Master Prompt
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Select all text in the box above and copy it. This
                        prompt will generate your entire book when pasted into
                        AI.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      2
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Open Your AI Tool
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Open ChatGPT, Claude, or Gemini. Free tiers work for
                        shorter books; paid plans usually allow longer outputs
                        and better drafting quality for full-length manuscripts.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href="https://chat.openai.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors"
                        >
                          ChatGPT →
                        </a>
                        <a
                          href="https://claude.ai"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 transition-colors"
                        >
                          Claude →
                        </a>
                        <a
                          href="https://gemini.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
                        >
                          Gemini →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      3
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Generate Your Draft Manuscript
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Paste the prompt and press enter. The AI starts drafting
                        right away. Generation time depends on book length and
                        the tool's output limits. A longer book may take several
                        minutes and multiple "continue" steps.
                      </p>
                      <p className="text-green-600 text-xs mt-1 font-medium">
                        💡 Tip: If the AI stops mid-chapter, type "continue" to
                        resume. For cleanest output on long books, generate one
                        chapter at a time using the chapter-by-chapter
                        instructions inside your prompt.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      4
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Edit, Fact-check & Personalize
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Replace every [AUTHOR: ...] placeholder with your own
                        examples and verified evidence. Fact-check claims,
                        remove AI clichés, tighten the voice, and run a
                        plagiarism check before publishing.
                      </p>
                      <div className="mt-2">
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                          Time: several hours to several days depending on
                          length and quality
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      5
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Format Your Ebook
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Use Amazon Kindle Create (free) to professionally format
                        your ebook. It automatically handles chapters, page
                        breaks, and table of contents.
                      </p>
                      <div className="mt-2">
                        <a
                          href="https://www.amazon.com/Kindle-Create/b?node=18292298011"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                        >
                          Download Kindle Create →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      6
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Design Your Cover
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Create an eye-catching cover using Canva's free ebook
                        cover templates or Amazon's Cover Creator.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href="https://www.canva.com/templates/?query=ebook%20cover"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded hover:bg-pink-200 transition-colors"
                        >
                          Canva Templates →
                        </a>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          Size: 1600x2560px
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      7
                    </span>
                    <div className="ml-3">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Publish on Amazon KDP
                      </h4>
                      <p className="text-gray-600 text-xs mt-1">
                        Upload your formatted ebook and cover to Amazon KDP. Set
                        your price, choose categories, and publish to millions
                        of readers worldwide!
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href="https://kdp.amazon.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                        >
                          Go to KDP →
                        </a>
                        <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">
                          💰 KDP royalty options vary (35% / 70% depending on
                          price &amp; region)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-300">
                <h4 className="font-semibold text-green-800 text-sm mb-2">
                  Realistic Time Investment:
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">AI Drafting:</span>
                    <span className="font-semibold text-gray-900 ml-1">
                      minutes to longer (depends on length and tool limits)
                    </span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Editing:</span>
                    <span className="font-semibold text-gray-900 ml-1">
                      several hours to several days
                    </span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Formatting:</span>
                    <span className="font-semibold text-gray-900 ml-1">
                      30-90 minutes
                    </span>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <span className="text-gray-600">Publishing:</span>
                    <span className="font-semibold text-gray-900 ml-1">
                      30-60 minutes
                    </span>
                  </div>
                </div>
                <p className="text-green-700 text-xs mt-2 font-medium text-center">
                  📚 With focused effort you can have a polished draft ready to
                  upload soon, but sales depend on niche, quality, cover,
                  pricing, marketing, and reader demand. Publishing fast does
                  not guarantee sales.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={downloadAsPDF}
                className="flex-1 bg-red-600 text-white py-3 px-4 min-h-[44px] rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-semibold"
              >
                <Download size={18} />
                Download PDF
              </button>
              <button
                onClick={downloadAsWord}
                className="flex-1 bg-blue-700 text-white py-3 px-4 min-h-[44px] rounded-lg hover:bg-blue-800 transition-colors flex items-center justify-center gap-2 font-semibold"
              >
                <Download size={18} />
                Download Word
              </button>
            </div>

            {!effectiveIsPro && (
              <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 text-center">
                <h3 className="font-semibold text-gray-900 mb-2">
                  🎯 Want More From Your Ebook?
                </h3>
                <p className="text-gray-700 text-sm mb-3">
                  Upgrade to Pro ($19 for 30 days, no auto-renewal) for up to 15
                  chapters, 8 advanced AI prompts, and marketing templates!
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={handleUpgrade}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 min-h-[44px] rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium"
                  >
                    Upgrade to Pro - $19
                  </button>
                  <button
                    onClick={() => openRestoreModal()}
                    className="bg-gray-600 text-white px-4 py-3 min-h-[44px] rounded-lg hover:bg-gray-700 transition-colors font-medium"
                  >
                    Restore Purchase
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen text-white relative">
      {appNotice && (
        <ModalPortal>
          <div
            className="modal-backdrop-enter fixed top-4 left-1/2 z-[10000] w-[calc(100%-2rem)] max-w-md -translate-x-1/2"
            role={appNotice.type === "error" ? "alert" : "status"}
            aria-live={appNotice.type === "error" ? "assertive" : "polite"}
          >
            <div
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 shadow-2xl backdrop-blur ${
                appNotice.type === "error"
                  ? "border-red-300 bg-red-50 text-red-900"
                  : "border-green-300 bg-green-50 text-green-900"
              }`}
            >
              {appNotice.type === "error" ? (
                <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 text-sm font-semibold">
                {appNotice.message}
              </div>
              <button
                type="button"
                onClick={dismissAppNotice}
                className="min-h-[32px] min-w-[32px] rounded-md text-current opacity-70 transition-opacity hover:opacity-100"
                aria-label="Dismiss notification"
              >
                <X size={16} className="mx-auto" />
              </button>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* Animated Background with Fade-in Effect */}
      <div
        className={`fixed inset-0 -z-30 transition-opacity duration-2000 ease-out ${
          backgroundLoaded ? "opacity-100" : "opacity-20"
        }`}
        style={{
          background:
            "linear-gradient(to bottom right, #0c0a18, #0f0a1f, #000000)",
        }}
      >
        {/* Animated gradient overlay that pulses on load */}
        <div
          className={`absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/40 transition-all duration-2500 ease-out ${
            backgroundLoaded ? "opacity-100" : "opacity-30"
          }`}
          style={{
            animation: backgroundLoaded ? "gradientPulse 4s ease-out" : "none",
          }}
        />

        {/* Drifting brand-color aurora blobs, adds depth and motion while
            keeping the existing purple gradient the brand is built on. */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="aurora-blob animate-aurora-slow"
            style={{
              top: "-12%",
              left: "-8%",
              width: "46vw",
              height: "46vw",
              background:
                "radial-gradient(circle at 30% 30%, rgba(139,92,246,0.55), transparent 60%)",
            }}
          />
          <div
            className="aurora-blob animate-aurora-slow-2"
            style={{
              top: "18%",
              right: "-12%",
              width: "42vw",
              height: "42vw",
              background:
                "radial-gradient(circle at 70% 40%, rgba(59,130,246,0.5), transparent 60%)",
            }}
          />
          <div
            className="aurora-blob animate-aurora-slow"
            style={{
              bottom: "-16%",
              left: "26%",
              width: "40vw",
              height: "40vw",
              background:
                "radial-gradient(circle at 50% 50%, rgba(232,121,249,0.4), transparent 62%)",
              animationDelay: "-8s",
            }}
          />
        </div>
      </div>

      {/* Overlay for better text readability */}
      <div className="fixed inset-0 bg-black/20 -z-10"></div>

      {/* Header Navigation */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ease-out ${
          isScrolled
            ? "bg-gray-950/80 md:bg-gray-900/90 backdrop-blur-md border-b border-white/10 md:border-gray-800/50 shadow-lg"
            : "bg-transparent border-b border-transparent"
        } ${
          contentLoaded
            ? "opacity-100 transform translate-y-0"
            : "opacity-0 transform -translate-y-8"
        }`}
      >
        <div className="w-full px-5 sm:px-6 md:px-8 lg:px-10">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo and Brand - Mobile Optimized */}
            <div className="flex items-center">
              <button
                onClick={() => {
                  trackButtonClick("logo_click", "header_navigation");
                  setCurrentStep(0);
                  // Reset the scroll state to ensure header is transparent
                  setIsScrolled(false);
                  // Set flag to scroll to top after page loads
                  localStorage.setItem("scrollToTop", "true");
                  // Refresh the page first, then scroll to top will happen after load
                  window.location.reload();
                }}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <img
                  src={logo}
                  alt=""
                  className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 mr-2 object-contain"
                />
                <div className="flex items-center">
                  <span className="text-xs sm:text-sm md:text-lg font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    AI Ebook Planner
                  </span>
                  {effectiveIsPro ? (
                    <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-fuchsia-500 px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-white shadow-[0_0_12px_rgba(168,85,247,0.55)]">
                      <Sparkles size={9} />
                      PRO
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white/90 backdrop-blur-sm">
                      FREE
                    </span>
                  )}
                </div>
              </button>
            </div>

            {/* Navigation Menu - Hidden on mobile, compact spacing on larger screens */}
            <nav
              aria-label="Main"
              className="hidden md:flex items-center ml-auto mr-3 lg:mr-5 space-x-2 lg:space-x-3 xl:space-x-4"
            >
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  trackButtonClick("home_page", "header_navigation");
                  // Reset the scroll state to ensure header is transparent
                  setIsScrolled(false);
                  // Set flag to scroll to top after page loads
                  localStorage.setItem("scrollToTop", "true");
                  // Refresh the page first, then scroll to top will happen after load
                  window.location.reload();
                }}
                className="px-3 py-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
              >
                HOME
              </a>
              <Link
                to="/about"
                onClick={() => {
                  trackButtonClick("about_page", "header_navigation");
                  trackPageView("about", "legal_pages");
                }}
                className="px-3 py-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
              >
                ABOUT
              </Link>
              <Link
                to="/pricing"
                onClick={() => {
                  trackButtonClick("pricing_page", "header_navigation");
                  trackUpgradeInterest("pricing_nav_click");
                }}
                className="px-3 py-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
              >
                PRICING
              </Link>
              <Link
                to="/contact"
                onClick={() => {
                  trackButtonClick("contact_page", "header_navigation");
                  trackPageView("contact", "legal_pages");
                }}
                className="px-3 py-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
              >
                CONTACT
              </Link>
              <a
                href="https://www.facebook.com/aiebookplanner/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackButtonClick("community_link", "header_navigation")
                }
                className="px-3 py-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-all text-sm font-medium"
              >
                COMMUNITY
              </a>
            </nav>

            {/* Mobile Menu Button - Enhanced Touch Target */}
            <div
              className={`md:hidden transition-all duration-1000 ease-out ${
                contentLoaded
                  ? "opacity-100 transform translate-y-0"
                  : "opacity-0 transform translate-y-8"
              }`}
            >
              <button
                onClick={() => {
                  trackButtonClick("mobile_menu_toggle", "header_navigation");
                  setShowMobileMenu(!showMobileMenu);
                }}
                className="text-gray-300 hover:text-white transition-colors p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-800"
                aria-label="Toggle mobile menu"
              >
                {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>

            {/* Navigation Actions - Hidden on mobile, compact on larger screens */}
            <div
              className={`hidden md:flex items-center space-x-2 transition-all duration-1000 ease-out ${
                contentLoaded
                  ? "opacity-100 transform translate-y-0"
                  : "opacity-0 transform translate-y-8"
              }`}
            >
              {/* Action Buttons - Enhanced touch targets */}
              <button
                onClick={() => {
                  trackButtonClick("restart", "header_navigation");
                  handleRestart();
                }}
                aria-label="Restart"
                className="bg-gray-800 border border-gray-700 text-white px-2 py-2 min-h-[44px] rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center gap-1 text-xs"
              >
                <RotateCcw size={12} />
                <span className="hidden lg:inline">RESTART</span>
              </button>

              {/* Desktop "PLANS" button removed, the PRICING nav link above
                  already opens the plans, so this was redundant. */}

              {!currentUser ? (
                <button
                  onClick={() => {
                    trackButtonClick("sign_in", "header_navigation");
                    openLoginModal();
                  }}
                  className="bg-white border border-gray-300 text-gray-800 px-2 py-2 min-h-[44px] rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center gap-1 font-medium text-xs"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="hidden lg:inline">SIGN IN</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      trackButtonClick("account_status", "header_navigation");
                      openLoginModal();
                    }}
                    className="bg-gray-950/80 border border-gray-700 text-white px-3 py-2 min-h-[44px] rounded-lg hover:bg-gray-800 transition-all duration-200 flex items-center gap-2 text-xs max-w-[260px]"
                    title={`Signed in as ${signedInEmailLabel}`}
                  >
                    <Mail size={13} className="text-blue-300 flex-shrink-0" />
                    <span className="truncate max-w-[150px]">
                      {signedInEmailLabel}
                    </span>
                    <span
                      className={`flex-shrink-0 rounded-full px-2 py-0.5 font-bold ${
                        effectiveIsPro
                          ? "bg-green-400 text-black"
                          : "bg-gray-700 text-gray-200"
                      }`}
                    >
                      {signedInPlanLabel}
                    </span>
                  </button>

                  {!effectiveIsPro && (
                    <button
                      onClick={() => {
                        trackButtonClick(
                          "restore_purchase",
                          "header_navigation"
                        );
                        // Clear any pre-filled data for security
                        openRestoreModal({ clearFields: true });
                      }}
                      className="bg-gray-800 border border-gray-700 text-white px-2 py-2 min-h-[44px] rounded-lg hover:bg-gray-700 transition-all duration-200 text-xs"
                    >
                      RESTORE
                    </button>
                  )}

                  <button
                    onClick={() =>
                      handleSignOut("sign_out", "header_navigation")
                    }
                    className="bg-gray-800 border border-gray-700 text-white px-2 py-2 min-h-[44px] rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center gap-1 text-xs"
                  >
                    <LogOut size={12} />
                    <span className="hidden lg:inline">SIGN OUT</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Menu Overlay - Full Screen Mobile Experience */}
        {showMobileMenu && (
          <>
            {/* Full Screen Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 md:hidden" />

            {/* Full Screen Mobile Menu Overlay */}
            <div className="mobile-menu-container fixed inset-0 w-full h-full bg-[#1a1a2e] z-50 md:hidden flex flex-col">
              {/* Menu Header with Top-Right Close Button */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
                <div className="flex items-center">
                  <img
                    src={logo}
                    alt=""
                    className="w-8 h-8 mr-3 object-contain"
                  />
                  <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                    AI Ebook Planner
                  </span>
                  {effectiveIsPro && (
                    <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-fuchsia-500 px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-white shadow-[0_0_12px_rgba(168,85,247,0.55)]">
                      <Sparkles size={9} />
                      PRO
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    trackButtonClick("close_mobile_menu", "mobile_menu");
                    setShowMobileMenu(false);
                  }}
                  className="p-3 text-white hover:text-gray-300 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-800/50"
                  aria-label="Close mobile menu"
                >
                  <X size={28} />
                </button>
              </div>

              {/* User Status */}
              {currentUser && (
                <div className="px-4 sm:px-6 py-4 bg-gray-800 border-b border-gray-700">
                  <div className="text-sm text-gray-300 mb-1 break-all">
                    Signed in as {signedInEmailLabel}
                  </div>
                  {effectiveIsPro ? (
                    <div className="text-green-400 text-sm font-medium">
                      PRO USER • {proStatusLabel}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">
                      FREE VERSION • 3 CHAPTERS • 1 BASIC PROMPT
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Links - Vertically Stacked and Center-Aligned */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
                <nav
                  aria-label="Main"
                  className="w-full max-w-sm mx-auto space-y-4"
                >
                  <button
                    onClick={() => {
                      trackButtonClick("mobile_home", "mobile_menu");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 text-white hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors font-bold text-lg text-center min-h-[44px] border border-gray-700 hover:border-blue-500"
                  >
                    <span>HOME</span>
                  </button>

                  <Link
                    to="/about"
                    onClick={() => {
                      trackButtonClick("mobile_about", "mobile_menu");
                      trackPageView("about", "legal_pages");
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 text-white hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors font-bold text-lg text-center min-h-[44px] border border-gray-700 hover:border-blue-500"
                  >
                    <span>ABOUT</span>
                  </Link>

                  <Link
                    to="/pricing"
                    onClick={() => {
                      trackButtonClick("mobile_pricing", "mobile_menu");
                      trackUpgradeInterest("mobile_menu_pricing");
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 text-white hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors font-bold text-lg text-center min-h-[44px] border border-gray-700 hover:border-blue-500"
                  >
                    <span>PRICING</span>
                  </Link>

                  <Link
                    to="/contact"
                    onClick={() => {
                      trackButtonClick("mobile_contact", "mobile_menu");
                      trackPageView("contact", "legal_pages");
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 text-white hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors font-bold text-lg text-center min-h-[44px] border border-gray-700 hover:border-blue-500"
                  >
                    <span>CONTACT</span>
                  </Link>

                  <a
                    href="https://www.facebook.com/aiebookplanner/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackButtonClick("mobile_community", "mobile_menu");
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 text-white hover:text-blue-300 hover:bg-gray-800/50 rounded-lg transition-colors font-bold text-lg text-center min-h-[44px] border border-gray-700 hover:border-blue-500"
                  >
                    <span>COMMUNITY</span>
                  </a>
                </nav>

                {/* Action Buttons - Mobile optimized with consistent styling */}
                <div className="mt-10 space-y-3">
                  <button
                    onClick={() => {
                      trackButtonClick("mobile_restart", "mobile_menu");
                      handleRestart();
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-4 bg-gray-800 border border-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors font-bold text-lg min-h-[44px]"
                  >
                    <RotateCcw size={20} className="mr-3" />
                    RESTART
                  </button>

                  <button
                    onClick={() => {
                      trackButtonClick("mobile_view_plans", "mobile_menu");
                      trackUpgradeInterest("mobile_menu_view_plans");
                      if (showPricingPlans) {
                        setShowPricingPlans(false);
                      } else {
                        // Scroll to top before opening modal
                        window.scrollTo({ top: 0, behavior: "instant" });
                        setShowPricingPlans(true);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all font-bold text-lg min-h-[44px] flex items-center justify-center w-full px-6 py-4"
                  >
                    <Target size={20} className="mr-3" />
                    VIEW PLANS
                  </button>

                  {!currentUser ? (
                    <button
                      onClick={() => {
                        trackButtonClick("mobile_sign_in", "mobile_menu");
                        openLoginModal();
                      }}
                      className="flex items-center justify-center w-full px-6 py-4 bg-white border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition-colors font-bold text-lg min-h-[44px]"
                    >
                      <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      SIGN IN WITH GOOGLE
                    </button>
                  ) : !effectiveIsPro ? (
                    <div className="space-y-3">
                      <button
                        onClick={() => {
                          trackButtonClick("mobile_restore_pro", "mobile_menu");
                          // Clear any pre-filled data for security
                          openRestoreModal({ clearFields: true });
                        }}
                        className="flex items-center justify-center w-full px-6 py-4 bg-gray-800 border border-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors font-bold text-lg min-h-[44px]"
                      >
                        <RefreshCw size={20} className="mr-3" />
                        RESTORE PRO ACCESS
                      </button>
                      <button
                        onClick={() =>
                          handleSignOut("mobile_sign_out", "mobile_menu")
                        }
                        className="flex items-center justify-center w-full px-6 py-4 bg-gray-800 border border-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors font-bold text-lg min-h-[44px]"
                      >
                        <LogOut size={20} className="mr-3" />
                        SIGN OUT
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        handleSignOut("mobile_sign_out", "mobile_menu")
                      }
                      className="flex items-center justify-center w-full px-6 py-4 bg-gray-800 border border-gray-700 text-white rounded-lg hover:bg-gray-700 transition-colors font-bold text-lg min-h-[44px]"
                    >
                      <LogOut size={20} className="mr-3" />
                      SIGN OUT
                    </button>
                  )}
                </div>

                {/* Support Link */}
                <div
                  className={`px-4 sm:px-6 py-4 border-t border-gray-700 mt-4 transition-all duration-1000 ease-out ${
                    contentLoaded
                      ? "opacity-100 transform translate-y-0"
                      : "opacity-0 transform translate-y-8"
                  }`}
                >
                  <button
                    onClick={() => {
                      window.open(
                        "mailto:aiebookplanner@gmail.com?subject=AI Ebook Planning Tool Support&body=Hello Support Team,%0D%0A%0D%0AI need help with:%0D%0A%0D%0A[Please describe your issue here]%0D%0A%0D%0AThank you!"
                      );
                      setShowMobileMenu(false);
                    }}
                    className="flex items-center justify-center w-full px-6 py-3 text-white hover:text-blue-300 transition-colors text-lg font-bold min-h-[44px] rounded-lg hover:bg-gray-800/50 border border-gray-700 hover:border-blue-500"
                  >
                    <Mail size={20} className="mr-3" />
                    CONTACT SUPPORT
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      <main id="main-content">
        {verificationBlockingState.blocked && (
          <div className="relative z-40 max-w-6xl mx-auto px-5 sm:px-6 md:px-8 lg:px-10 pt-4">
            <div className="rounded-lg border border-yellow-400/40 bg-yellow-950/80 p-4 text-yellow-50 shadow-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <AlertCircle
                  size={20}
                  className="text-yellow-300 flex-shrink-0"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold">
                    Verify your email to protect this account.
                  </p>
                  <p className="text-xs text-yellow-100/90 mt-1">
                    Signed in as {signedInEmailLabel}. Check your inbox and spam
                    folder, then refresh your account status.
                  </p>
                </div>
                <button
                  onClick={openLoginModal}
                  className="rounded-md bg-yellow-300 px-3 py-2 text-xs font-bold text-black hover:bg-yellow-200 transition-colors"
                >
                  Account Status
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content - Enhanced Mobile Responsiveness */}
        {/* No entrance animation on this container: it holds the hero video and
          its fade mask, and sliding or fading that layer made browsers draw a
          one-pixel line at the hero seam on every load. The hero text and the
          content below animate in on their own wrappers instead. The static
          transform stays because the inline fixed modals position against it. */}
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-8 lg:px-10 py-4 sm:py-6 md:py-8 min-h-screen opacity-100 transform translate-y-0">
          {/* Mobile-specific CSS fixes */}
          <style>{`
          /* Prevent white screen on mobile */
          @media (max-width: 768px) {
            body {
              min-height: calc(var(--mobile-hero-vh, 1vh) * 100) !important;
              overflow-x: clip !important;
              -webkit-text-size-adjust: 100%;
              -ms-text-size-adjust: 100%;
            }
            
            #root {
              min-height: calc(var(--mobile-hero-vh, 1vh) * 100) !important;
            }
            
            /* Ensure proper mobile rendering */
            .mobile-fix {
              -webkit-transform: translateZ(0);
              -moz-transform: translateZ(0);
              -ms-transform: translateZ(0);
              -o-transform: translateZ(0);
              transform: translateZ(0);
              -webkit-backface-visibility: hidden;
              -moz-backface-visibility: hidden;
              -ms-backface-visibility: hidden;
              -o-backface-visibility: hidden;
              backface-visibility: hidden;
              will-change: transform;
            }
            
            /* Fix mobile step layout */
            .step-grid {
              display: grid !important;
              grid-template-columns: 1fr !important;
              gap: 1rem !important;
            }
            
            /* Mobile-specific step improvements */
            .step-grid > div {
              margin-bottom: 1rem !important;
            }
            
            /* Ensure mobile text readability */
            .step-grid .text-xs {
              font-size: 0.75rem !important;
              line-height: 1.2 !important;
            }
            
            /* Fix mobile menu layout and prevent scrambling */
            .mobile-menu-container {
              display: flex !important;
              flex-direction: column !important;
              overflow: hidden !important;
            }
            
            .mobile-menu-container .flex-1 {
              flex: 1 1 auto !important;
              min-height: 0 !important;
            }
            
            .mobile-menu-container .overflow-y-auto {
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
            
            /* Ensure proper mobile menu button spacing */
            .mobile-menu-container button,
            .mobile-menu-container a {
              width: 100% !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            
            /* Fix mobile menu text alignment */
            .mobile-menu-container span {
              text-align: center !important;
              width: 100% !important;
            }
            
                         /* Ensure proper spacing between menu sections */
             .mobile-menu-container .mt-10 {
               margin-top: 2.5rem !important;
             }
             
             .mobile-menu-container .mt-4 {
               margin-top: 1rem !important;
             }
            
            /* Fix mobile menu button heights and spacing */
            .mobile-menu-container button,
            .mobile-menu-container a {
              height: auto !important;
              min-height: 44px !important;
              padding: 1rem 1.5rem !important;
              border-radius: 0.5rem !important;
            }
            
                         /* Ensure mobile menu content doesn't overflow */
             .mobile-menu-container .overflow-y-auto {
               padding-bottom: 1rem !important;
             }
             

          }
          
          /* Ensure proper rendering on all devices */
          * {
            box-sizing: border-box;
          }
          
          html, body {
            margin: 0;
            padding: 0;
            color: #fff;
            font-family: "Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          
          /* Additional mobile optimizations */
          @media (max-width: 640px) {
            .step-grid {
              gap: 0.75rem !important;
            }
            
            .step-grid > div {
              margin-bottom: 0.75rem !important;
            }
          }
        `}</style>
          {/* ===================================================================
            FULL-BLEED CINEMATIC HERO, a looping, muted hero video fills the top
            of the page edge-to-edge (pulled up under the transparent header) and
            fades into the content below. The landscape still image is the poster,
            so there is no blank frame before the video plays. The web-optimized
            MP4 + WebM live in public/hero/ (transcoded down from the 4K source);
            to refresh them, re-run the ffmpeg transcode into that folder.
            =================================================================== */}
          <section
            className="relative w-screen overflow-hidden -mt-[72px] sm:-mt-[88px] md:-mt-[96px]"
            style={{
              marginLeft: "calc(50% - 50vw)",
              minHeight: "calc(var(--mobile-hero-vh, 1vh) * 86)",
            }}
          >
            {/* Background layers share one fade mask (see HERO_LAYER_FADE). */}
            <div className="absolute inset-0" style={HERO_LAYER_FADE}>
              <video
                autoPlay
                muted
                loop
                playsInline
                poster={heroLandscape}
                aria-label="A glowing book of ideas blooming into light"
                className="absolute inset-x-0 w-full object-cover object-center select-none"
                style={HERO_VIDEO_BLEED}
              >
                <source
                  src={`${process.env.PUBLIC_URL}/hero/hero.webm`}
                  type="video/webm"
                />
                <source
                  src={`${process.env.PUBLIC_URL}/hero/hero.mp4`}
                  type="video/mp4"
                />
              </video>
              {/* Darken the left for text legibility; the glowing book stays bright
              on the right. Plus a soft top (header) and a fade into the page. */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, rgba(12,10,24,0.82) 0%, rgba(12,10,24,0.35) 45%, rgba(12,10,24,0) 70%)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />
              <div
                className="absolute inset-x-0 bottom-0 h-1/2"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(12,10,24,0) 0%, rgba(12,10,24,0.55) 55%, #0c0a18 100%)",
                }}
              />
            </div>
            {/* Overlaid hero content, left-aligned so it sits over the darker
              left side and lets the glowing book breathe on the right. */}
            <div
              className={`relative z-10 w-full flex flex-col items-start justify-end px-5 pt-28 pb-12 text-left sm:px-6 sm:pb-16 md:px-8 md:pb-20 lg:px-10 transition-all duration-1000 ease-out ${
                contentLoaded
                  ? "opacity-100 transform translate-y-0"
                  : "opacity-0 transform translate-y-8"
              }`}
              style={{ minHeight: "calc(var(--mobile-hero-vh, 1vh) * 86)" }}
            >
              <div className="max-w-xl">
                <div className="mb-5 sm:mb-6">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs sm:text-sm font-medium text-purple-100 backdrop-blur-md shadow-glow">
                    <Sparkles size={14} className="text-purple-300" />
                    Free to start: a prompt that makes any AI write your book
                  </span>
                </div>
                <h1 className="font-display mb-4 text-4xl font-extrabold leading-[1.05] tracking-tight sm:mb-6 sm:text-5xl md:text-6xl lg:text-7xl">
                  <span className="bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent animate-gradient-x">
                    Create Professional Ebooks
                  </span>
                </h1>
                <p className="mb-6 max-w-lg text-base font-light leading-relaxed text-gray-100 drop-shadow sm:mb-8 sm:text-lg md:text-xl">
                  Answer a few quick questions and get{" "}
                  <span className="font-medium text-purple-200">
                    one ready-to-paste prompt
                  </span>
                  . Drop it into any AI you want, from ChatGPT to Claude to
                  DeepSeek, and it writes your entire book for you: ready to
                  edit, own, and publish.
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {[
                    "3 free chapters",
                    "No credit card to start",
                    "Export-ready AI prompts",
                  ].map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs text-gray-100 backdrop-blur-md sm:text-sm"
                    >
                      <CheckCircle size={14} className="text-green-400" />
                      {label}
                    </span>
                  ))}
                </div>
                <div className="mt-6 sm:mt-7">
                  <button
                    onClick={() => {
                      trackButtonClick("hero_get_started", "homepage_hero");
                      const formSection = document.querySelector(".step-grid");
                      if (formSection) {
                        formSection.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                    className="btn-sheen inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-3 text-sm font-semibold text-white shadow-glow transition-all duration-200 hover:from-blue-600 hover:to-purple-600 sm:text-base"
                  >
                    <Sparkles size={16} />
                    Get Started Free
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Entrance for everything below the hero: the same fade and slide the
            whole page used to have. */}
          <div
            className={`transition-all duration-1000 ease-out ${
              contentLoaded
                ? "opacity-100 transform translate-y-0"
                : "opacity-0 transform translate-y-8"
            }`}
          >
            {/* Animated planner preview — bridges the hero into the real workflow
            and sits directly above the planner steps below. */}
            <EbookOrbitShowcase />

            {/* Account status (normal width); the planner steps sit right below the hero. */}
            <div className="text-center px-2 sm:px-4 md:px-0 pt-3 sm:pt-4 mb-6 sm:mb-8">
              {/* Homepage promo video removed; the planner steps below now sit directly under the hero. */}

              {/* User Status Display - Mobile Optimized */}
              <div className="text-center px-2 sm:px-4 md:px-0">
                {currentUser ? (
                  <div className="space-y-1">
                    <span className="text-gray-400 text-xs sm:text-sm break-all">
                      Signed in as {signedInEmailLabel}
                    </span>
                    {effectiveIsPro ? (
                      <div className="text-green-400 text-xs sm:text-sm font-medium">
                        PRO USER • {proStatusLabel}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-xs sm:text-sm">
                        FREE VERSION • 3 CHAPTERS • 1 BASIC PROMPT
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs sm:text-sm">
                    SIGN IN TO SAVE YOUR PRO STATUS ACROSS DEVICES
                  </span>
                )}
              </div>
            </div>

            {/* Progress Steps - Fixed Icon Overlap and Mobile Responsiveness */}
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8 sm:mb-10 md:mb-12 step-grid mobile-fix transition-all duration-1000 ease-out ${
                stepsLoaded
                  ? "opacity-100 transform translate-y-0"
                  : "opacity-0 transform translate-y-8"
              }`}
            >
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                const isNextStep = index === currentStep + 1;

                // Determine animation class based on step state
                let animationClass = "";
                if (isActive) {
                  animationClass = "animate-active-step-pulse";
                } else if (isNextStep && !isCompleted) {
                  animationClass = "animate-next-step-hint";
                } else if (isCompleted) {
                  animationClass = "animate-completed-glow";
                }

                return (
                  <div
                    key={index}
                    className="flex flex-col items-center text-center relative group"
                  >
                    {/* Icon Container - Fixed positioning to prevent overlap */}
                    <div
                      className={`
                  w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mb-3 relative z-20 transition-all duration-300
                  backdrop-blur-sm border-2 border-white/20 shadow-lg mx-auto
                  ${animationClass}
                  ${
                    isActive
                      ? "bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-glow border-white/30"
                      : isCompleted
                        ? "bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-xl border-white/30"
                        : "bg-white/5 text-gray-300"
                  }
                `}
                    >
                      {isCompleted ? (
                        <CheckCircle size={18} className="sm:w-6 sm:h-6" />
                      ) : (
                        <StepIcon size={18} className="sm:w-6 sm:h-6" />
                      )}
                    </div>

                    {/* Title Box - Positioned below icon with proper spacing */}
                    <div className="w-full mb-2">
                      <div
                        className={`text-xs sm:text-sm font-bold transition-all duration-300 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg backdrop-blur-sm border ${
                          isActive
                            ? "text-white bg-gradient-to-r from-blue-500 to-purple-600 border-blue-400/50 shadow-glow"
                            : isCompleted
                              ? "text-white bg-gradient-to-r from-green-500 to-emerald-600 border-green-400/50 shadow-md"
                              : "text-gray-200 bg-white/5 border-white/10"
                        }`}
                      >
                        {step.title}
                      </div>
                    </div>

                    {/* Description Box - Positioned below title */}
                    <div className="w-full">
                      <div
                        className={`text-xs sm:text-sm font-medium transition-all duration-300 px-2 sm:px-3 py-2 rounded-lg backdrop-blur-sm border ${
                          isActive
                            ? "text-white bg-white/10 border-white/20"
                            : "text-gray-400 bg-white/5 border-white/10"
                        }`}
                      >
                        {step.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CSS Animations for Background and Progress Steps */}
            <style>{`
           /* Background fade-in and gradient pulse animations */
           @keyframes gradientPulse {
             0% {
               opacity: 0.3;
               transform: scale(1.02);
             }
             25% {
               opacity: 0.6;
               transform: scale(1.01);
             }
             50% {
               opacity: 0.8;
               transform: scale(1.005);
             }
             75% {
               opacity: 0.9;
               transform: scale(1.002);
             }
             100% {
               opacity: 1;
               transform: scale(1);
             }
           }

           /* Smooth transition for opacity changes */
           .transition-opacity {
             transition-property: opacity;
           }
           
           .duration-1000 {
             transition-duration: 1000ms;
           }
           
           .duration-2000 {
             transition-duration: 2000ms;
           }
           
           .ease-out {
             transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
           }

          @keyframes activeStepPulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7), 0 4px 14px 0 rgba(37, 99, 235, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 0 0 8px rgba(37, 99, 235, 0.2), 0 6px 20px 0 rgba(37, 99, 235, 0.4);
            }
          }

          @keyframes nextStepHint {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4), 0 2px 8px 0 rgba(59, 130, 246, 0.2);
            }
            50% {
              transform: scale(1.02);
              box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15), 0 4px 12px 0 rgba(59, 130, 246, 0.25);
            }
          }

          @keyframes completedGlow {
            0%, 100% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.3), 0 2px 8px 0 rgba(34, 197, 94, 0.2);
            }
          }

          .animate-active-step-pulse {
            animation: activeStepPulse 2s ease-in-out infinite;
          }

          .animate-next-step-hint {
            animation: nextStepHint 3s ease-in-out infinite;
          }

          .animate-completed-glow {
            animation: completedGlow 1s ease-in-out;
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.3), 0 2px 8px 0 rgba(34, 197, 94, 0.2);
          }

          @media (prefers-reduced-motion: reduce) {
            .animate-active-step-pulse,
            .animate-next-step-hint,
            .animate-completed-glow {
              animation: none;
            }

            .animate-active-step-pulse {
              box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7), 0 4px 14px 0 rgba(37, 99, 235, 0.3);
            }

            .animate-next-step-hint {
              box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4), 0 2px 8px 0 rgba(59, 130, 246, 0.2);
            }

            .animate-completed-glow {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.3), 0 2px 8px 0 rgba(34, 197, 94, 0.2);
            }
          }

          /* Static Background - No animations needed */

          /* Floating Geometric Patterns */
          @keyframes floatSlow {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
            }
          }

          @keyframes floatMedium {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-15px) rotate(90deg);
            }
          }

          @keyframes floatFast {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(45deg);
            }
          }

          .animate-float-slow {
            animation: floatSlow 8s ease-in-out infinite;
          }

          .animate-float-medium {
            animation: floatMedium 6s ease-in-out infinite;
          }

          .animate-float-fast {
            animation: floatFast 4s ease-in-out infinite;
          }

          /* Floating Particles */
          @keyframes particleFloat1 {
            0%, 100% {
              transform: translate(0px, 0px);
              opacity: 0.3;
            }
            25% {
              transform: translate(10px, -15px);
              opacity: 0.7;
            }
            50% {
              transform: translate(-5px, -25px);
              opacity: 0.5;
            }
            75% {
              transform: translate(-10px, -10px);
              opacity: 0.8;
            }
          }

          @keyframes particleFloat2 {
            0%, 100% {
              transform: translate(0px, 0px);
              opacity: 0.4;
            }
            33% {
              transform: translate(-12px, -18px);
              opacity: 0.6;
            }
            66% {
              transform: translate(8px, -12px);
              opacity: 0.9;
            }
          }

          @keyframes particleFloat3 {
            0%, 100% {
              transform: translate(0px, 0px);
              opacity: 0.2;
            }
            40% {
              transform: translate(15px, -20px);
              opacity: 0.8;
            }
            80% {
              transform: translate(-8px, -15px);
              opacity: 0.4;
            }
          }

          @keyframes particleFloat4 {
            0%, 100% {
              transform: translate(0px, 0px);
              opacity: 0.5;
            }
            30% {
              transform: translate(-10px, -12px);
              opacity: 0.3;
            }
            70% {
              transform: translate(12px, -18px);
              opacity: 0.7;
            }
          }

          .animate-particle-float-1 {
            animation: particleFloat1 12s ease-in-out infinite;
          }

          .animate-particle-float-2 {
            animation: particleFloat2 15s ease-in-out infinite;
          }

          .animate-particle-float-3 {
            animation: particleFloat3 10s ease-in-out infinite;
          }

          .animate-particle-float-4 {
            animation: particleFloat4 13s ease-in-out infinite;
          }

          /* Accessibility - Respect reduced motion preferences */
          @media (prefers-reduced-motion: reduce) {
            .animate-gradient-shift,
            .animate-float-slow,
            .animate-float-medium,
            .animate-float-fast,
            .animate-particle-float-1,
            .animate-particle-float-2,
            .animate-particle-float-3,
            .animate-particle-float-4 {
              animation: none;
            }

            .animate-gradient-shift {
              background: linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #0d9488 100%);
            }
          }

          /* Mobile Optimization - Reduce animation intensity */
          @media (max-width: 768px) {
            .animate-gradient-shift {
              animation-duration: 60s;
            }

            .animate-float-slow,
            .animate-float-medium,
            .animate-float-fast {
              animation-duration: 12s;
            }

            .animate-particle-float-1,
            .animate-particle-float-2,
            .animate-particle-float-3,
            .animate-particle-float-4 {
              animation-duration: 20s;
            }
          }

          /* Enhanced Mobile Responsiveness */
          @media (max-width: 640px) {
            .mobile-menu-container {
              width: 100vw;
              max-width: 100vw;
              height: calc(var(--mobile-hero-vh, 1vh) * 100);
            }
          }

          @media (max-width: 480px) {
            .hero-title {
              font-size: 1.5rem;
              line-height: 2rem;
            }
            
            .hero-description {
              font-size: 0.875rem;
              line-height: 1.25rem;
            }
          }

          /* Mobile-First Touch Optimizations */
          @media (max-width: 768px) {
            /* Ensure all interactive elements meet touch target requirements */
            button, input, select, textarea, a {
              min-height: 44px;
              min-width: 44px;
            }

            /* Optimize form spacing for mobile */
            .space-y-4 > * + * {
              margin-top: 1rem;
            }

            .space-y-6 > * + * {
              margin-top: 1.5rem;
            }

            /* Improve mobile text readability */
            .text-sm {
              font-size: 0.875rem;
              line-height: 1.25rem;
            }

            .text-base {
              font-size: 1rem;
              line-height: 1.5rem;
            }

            /* Mobile-optimized spacing */
            .p-4 {
              padding: 1rem;
            }

            .p-6 {
              padding: 1.5rem;
            }

            .mb-4 {
              margin-bottom: 1rem;
            }

            .mb-6 {
              margin-bottom: 1.5rem;
            }
          }

          /* Small Mobile Optimizations */
          @media (max-width: 375px) {
            .text-2xl {
              font-size: 1.5rem;
              line-height: 2rem;
            }

            .text-3xl {
              font-size: 1.875rem;
              line-height: 2.25rem;
            }

            .p-4 {
              padding: 0.75rem;
            }

            .p-6 {
              padding: 1.25rem;
            }
          }

          /* Prevent Horizontal Overflow on Mobile */
          @media (max-width: 768px) {
            .mobile-menu-container,
            .mobile-menu-container * {
              max-width: 100vw;
              overflow-x: hidden;
            }
            
            /* Ensure all form elements are responsive */
            input, select, textarea, button {
              max-width: 100%;
              box-sizing: border-box;
            }
            
            /* Prevent text from overflowing */
            .text-center {
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
          }

          /* Custom Slider Styling for Mobile */
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          /* Mobile Navigation Menu Enhancements */
          @media (max-width: 768px) {
            .mobile-menu-container {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100vw;
              height: calc(var(--mobile-hero-vh, 1vh) * 100);
              z-index: 50;
              overflow-y: auto;
              overflow-x: hidden;
            }
            
            .mobile-menu-container nav {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 60vh;
            }
            
            .mobile-menu-container button,
            .mobile-menu-container a {
              width: 100%;
              max-width: 320px;
              margin: 0 auto;
            }
          }

          .slider::-moz-range-thumb {
            height: 20px;
            width: 20px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid #ffffff;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          }

          .slider::-webkit-slider-track {
            height: 8px;
            border-radius: 4px;
            background: #e5e7eb;
          }

          .slider::-moz-range-track {
            height: 8px;
            border-radius: 4px;
            background: #e5e7eb;
          }
        `}</style>

            {/* Step Content - Mobile Optimized with better readability and spacing */}
            <div className="glass-card p-4 sm:p-6 rounded-3xl mb-8 sm:mb-10 md:mb-12 min-h-80 sm:min-h-96 relative">
              <div
                className={`relative z-10 transition-all duration-1000 ease-out ${
                  contentLoaded
                    ? "opacity-100 transform translate-y-0"
                    : "opacity-0 transform translate-y-8"
                }`}
              >
                {/* Restore saved session banner */}
                {showProgressRestorePrompt && savedProgressData && (
                  <div className="mb-4 bg-indigo-500/10 border border-indigo-400/30 backdrop-blur-md rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <RotateCcw
                        size={20}
                        className="text-indigo-300 shrink-0"
                      />
                      <div>
                        <p className="text-white font-semibold text-sm">
                          Welcome back! You have a saved session.
                        </p>
                        <p className="text-indigo-200/80 text-xs mt-0.5">
                          {savedProgressData.formData?.topic
                            ? `"${savedProgressData.formData.topic}" · Step ${(savedProgressData.currentStep || 0) + 1} of 5`
                            : `Step ${(savedProgressData.currentStep || 0) + 1} of 5`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => {
                          if (savedProgressData.formData) {
                            const saved = savedProgressData.formData;
                            // Free plans allow 3 chapters; a session saved
                            // while Pro must not bring more back.
                            if (!effectiveIsPro && saved.chapters > 3) {
                              restoredChaptersRef.current = saved.chapters;
                              setFormData({ ...saved, chapters: 3 });
                            } else {
                              setFormData(saved);
                            }
                          }
                          if (typeof savedProgressData.currentStep === "number")
                            setCurrentStep(savedProgressData.currentStep);
                          if (savedProgressData.generatedOutline)
                            setGeneratedOutline(
                              savedProgressData.generatedOutline
                            );
                          if (Array.isArray(savedProgressData.generatedPrompts))
                            setGeneratedPrompts(
                              savedProgressData.generatedPrompts
                            );
                          setShowProgressRestorePrompt(false);
                          setSavedProgressData(null);
                        }}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow-glow"
                      >
                        Resume
                      </button>
                      <button
                        onClick={() => {
                          clearProgressFromStorage(
                            currentUser ? currentUser.uid : "guest"
                          );
                          setShowProgressRestorePrompt(false);
                          setSavedProgressData(null);
                        }}
                        className="border border-white/15 bg-white/5 text-gray-200 hover:bg-white/10 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        Start Fresh
                      </button>
                    </div>
                  </div>
                )}

                {/* Mobile: text box shown first, then the step title. The
                    title stays first in the page structure (headings before
                    their content); flex order puts the text box on top. */}
                <div
                  data-step-layout="phone"
                  className="flex flex-col sm:hidden"
                >
                  <div className="order-2 mt-4 sm:mt-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                      {steps[currentStep].title}
                    </h2>

                    {/* Fast AI Processing Indicator for Pro Users */}
                    {isGeneratingOutline && effectiveIsPro && (
                      <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-center mb-3">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                          <span className="text-blue-800 font-semibold text-lg">
                            🚀 Loading Premium AI Models
                          </span>
                        </div>
                        <div className="text-center">
                          <p className="text-blue-700 text-sm mb-2">
                            Generating your final prompt with advanced AI
                            technology
                          </p>
                          <div className="flex justify-center space-x-4 text-xs text-blue-600">
                            <span>Premium Requests</span>
                            <span>⚡ Priority Processing</span>
                            <span>🎯 Advanced AI Models</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Standard Processing Indicator for Free Users */}
                    {isGeneratingOutline && !effectiveIsPro && (
                      <div className="mb-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-center mb-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 mr-3"></div>
                          <span className="text-gray-800 font-medium">
                            Generating your ebook plan...
                          </span>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-600 text-sm">
                            Using standard AI processing
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="order-1">{renderStep("phone")}</div>
                </div>

                {/* Desktop: Header first, then text box */}
                <div data-step-layout="wide" className="hidden sm:block">
                  <h2 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                    {steps[currentStep].title}
                  </h2>

                  {/* Fast AI Processing Indicator for Pro Users */}
                  {isGeneratingOutline && effectiveIsPro && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-center mb-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                        <span className="text-blue-800 font-semibold text-lg">
                          🚀 Loading Premium AI Models
                        </span>
                      </div>
                      <div className="text-center">
                        <p className="text-blue-700 text-sm mb-2">
                          Generating your final prompt with advanced AI
                          technology
                        </p>
                        <div className="flex justify-center space-x-4 text-xs text-blue-600">
                          <span>Premium Requests</span>
                          <span>⚡ Priority Processing</span>
                          <span>🎯 Advanced AI Models</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Standard Processing Indicator for Free Users */}
                  {isGeneratingOutline && !effectiveIsPro && (
                    <div className="mb-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                      <div className="flex items-center justify-center mb-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600 mr-3"></div>
                        <span className="text-gray-800 font-medium">
                          Generating your ebook plan...
                        </span>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-600 text-sm">
                          Using standard AI processing
                        </p>
                      </div>
                    </div>
                  )}

                  {renderStep("wide")}
                </div>
              </div>
            </div>

            {/* Navigation Buttons - Mobile Optimized with Support Button Fix */}
            <div className="relative">
              {/* Support Button - Positioned separately on mobile */}
              <div className="block sm:hidden mb-4">
                <button
                  onClick={() =>
                    window.open(
                      "mailto:aiebookplanner@gmail.com?subject=AI Ebook Planning Tool Support&body=Hello Support Team,%0D%0A%0D%0AI need help with:%0D%0A%0D%0A[Please describe your issue here]%0D%0A%0D%0AThank you!"
                    )
                  }
                  className="w-full bg-white/5 border border-white/15 text-white px-4 py-3 min-h-[44px] rounded-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2 text-sm shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Mail size={16} />
                  <span>SUPPORT</span>
                </button>
              </div>

              {/* Step validation error */}
              {stepError && (
                <div className="bg-red-50 border border-red-300 rounded-lg px-4 py-3 flex items-center gap-2 mb-4">
                  <AlertCircle size={16} className="text-red-500 shrink-0" />
                  <p className="text-red-700 text-sm font-medium">
                    {stepError}
                  </p>
                </div>
              )}

              {/* Previous/Next Navigation */}
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                <button
                  onClick={() => {
                    trackButtonClick(
                      "previous_step",
                      `step_${currentStep}_to_${currentStep - 1}`
                    );
                    prevStep();
                  }}
                  disabled={currentStep === 0}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 min-h-[44px] rounded-lg transition-all duration-300 text-sm sm:text-base ${
                    currentStep === 0
                      ? "bg-white/5 text-gray-500 cursor-not-allowed backdrop-blur-sm border border-white/10"
                      : "bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white shadow-lg hover:shadow-xl transform hover:scale-105 border border-white/20"
                  }`}
                >
                  Previous
                </button>

                {/* Support button for desktop only */}
                <div className="hidden sm:flex flex-1 justify-center">
                  <button
                    onClick={() => {
                      trackButtonClick("contact_support", "navigation");
                      window.open(
                        "mailto:aiebookplanner@gmail.com?subject=AI Ebook Planning Tool Support&body=Hello Support Team,%0D%0A%0D%0AI need help with:%0D%0A%0D%0A[Please describe your issue here]%0D%0A%0D%0AThank you!"
                      );
                    }}
                    className="bg-white/5 border border-white/15 text-white px-4 sm:px-6 py-3 min-h-[44px] rounded-lg hover:bg-white/10 transition-all duration-300 flex items-center gap-2 text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <Mail size={16} />
                    <span className="hidden sm:inline">CONTACT SUPPORT</span>
                    <span className="sm:hidden">SUPPORT</span>
                  </button>
                </div>

                <button
                  onClick={() => {
                    if (currentStep === 2) {
                      trackButtonClick(
                        "generate_plan",
                        `step_${currentStep}_${formData.topic}`
                      );
                    } else {
                      trackButtonClick(
                        "next_step",
                        `step_${currentStep}_to_${currentStep + 1}`
                      );
                    }
                    nextStep();
                  }}
                  disabled={currentStep === steps.length - 1}
                  className={`w-full sm:w-auto px-4 sm:px-6 py-3 min-h-[44px] rounded-lg transition-all duration-300 text-sm sm:text-base ${
                    currentStep === steps.length - 1
                      ? "bg-white/5 text-gray-500 cursor-not-allowed backdrop-blur-sm border border-white/10"
                      : "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105"
                  }`}
                >
                  {currentStep === steps.length - 1 ? "Complete" : "Next"}
                </button>
              </div>
            </div>
          </div>

          {/* Pricing Plans Modal */}
          {showPricingPlans && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-3 sm:p-4 modal-backdrop pt-12"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pricing-plans-title"
            >
              <div
                className="bg-white rounded-2xl max-w-3xl w-full relative mx-4 modal-content shadow-2xl"
                style={{
                  maxHeight: "80vh",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* X Close Button */}
                <button
                  onClick={() => {
                    trackButtonClick("close_pricing_modal", "pricing_modal");
                    setShowPricingPlans(false);
                  }}
                  className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 w-8 h-8 rounded-full flex items-center justify-center transition-colors min-h-[44px]"
                  aria-label="Close modal"
                >
                  <X size={20} />
                </button>

                <div className="p-3 sm:p-4 md:p-5 flex-1 overflow-y-auto modal-scrollable-content">
                  {/* Header */}
                  <div className="text-center mb-3 sm:mb-4">
                    <h2
                      id="pricing-plans-title"
                      className="text-xl sm:text-2xl font-bold text-gray-900 mb-1"
                    >
                      Choose Your Plan
                    </h2>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Create professional ebooks with AI-powered content
                      generation
                    </p>
                  </div>

                  {/* Pricing Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
                    {/* Free Plan */}
                    <div
                      className={`bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 relative cursor-pointer transition-all duration-300 ${
                        freeCardGlow
                          ? "shadow-2xl shadow-green-500/50 border-green-400 transform scale-105"
                          : "hover:shadow-lg hover:border-green-300"
                      }`}
                      onClick={() => {
                        trackDemoInteraction("view_free_plan", "pricing_modal");
                        handleFreeCardClick();
                      }}
                    >
                      <div className="text-center mb-4 sm:mb-6">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                          Free
                        </h3>
                        <div className="mb-4">
                          <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                            $0
                          </span>
                          <span className="text-gray-500 ml-1">forever</span>
                        </div>
                        <p className="text-gray-600 text-sm">
                          Perfect for trying out the basics
                        </p>
                      </div>

                      {/* Free Features */}
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm">
                            3 Chapters
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm">
                            1 Basic AI Prompt
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm">
                            Standard Export
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm">
                            Basic Templates
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-green-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm">
                            Community Support
                          </span>
                        </div>
                      </div>

                      {/* Free Button */}
                      <button
                        className="w-full py-3 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors min-h-[44px]"
                        onClick={() => {
                          trackButtonClick("start_free_plan", "pricing_modal");

                          // Close modal immediately
                          setShowPricingPlans(false);

                          // Set guest user state for immediate free access
                          if (!currentUser) {
                            setIsGuestUser(true);
                          }

                          // Reset to beginning if needed
                          if (currentStep > 0) {
                            setCurrentStep(0);
                          }

                          // Scroll to top
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        {currentUser && !effectiveIsPro
                          ? "Your Current Plan"
                          : "Get Started Free"}
                      </button>
                    </div>

                    {/* Pro Plan */}
                    <div
                      className={`bg-white border-2 border-blue-500 rounded-xl p-4 sm:p-6 relative shadow-lg cursor-pointer transition-all duration-300 ${
                        proCardGlow
                          ? "shadow-2xl shadow-blue-500/50 border-blue-400 transform scale-105"
                          : "hover:shadow-xl hover:border-blue-400"
                      }`}
                      onClick={() => {
                        trackDemoInteraction("view_pro_plan", "pricing_modal");
                        trackUpgradeInterest("pricing_modal_pro_click");
                        handleProCardClick();
                      }}
                    >
                      {/* Recommended Badge */}
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-blue-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium">
                          Recommended
                        </span>
                      </div>

                      <div className="text-center mb-4 sm:mb-6 mt-2">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                          Pro
                        </h3>
                        <div className="mb-4">
                          <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                            $19
                          </span>
                          <span className="text-gray-500 ml-1">/ 30 days</span>
                        </div>
                        <p className="text-gray-600 text-sm">
                          One-time payment · no auto-renewal.
                        </p>
                      </div>

                      {/* Pro Features */}
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Up to 15 Chapters
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Fast Premium AI Requests
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            8+ Advanced AI Prompts
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            AI-Generated Chapter Titles
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            AI-Enhanced Descriptions
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Marketing Templates
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Priority Support
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Revenue Optimization Guide
                          </span>
                        </div>
                        <div className="flex items-center">
                          <CheckCircle
                            size={16}
                            className="text-blue-500 mr-3 flex-shrink-0"
                          />
                          <span className="text-gray-700 text-sm font-medium">
                            Launch Strategy Templates
                          </span>
                        </div>
                      </div>

                      {/* Pro Button - Conditional Rendering */}
                      <div className="space-y-3">
                        {!effectiveIsPro ? (
                          // Show upgrade button for non-Pro users
                          <button
                            onClick={() => {
                              trackButtonClick(
                                "upgrade_to_pro",
                                "pricing_modal"
                              );
                              trackUpgradeInterest(
                                "pricing_modal_upgrade_button"
                              );
                              handleUpgrade();
                            }}
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 min-h-[44px] rounded-xl transition-all text-sm font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105"
                          >
                            🚀 Upgrade to Pro - $19
                          </button>
                        ) : !effectiveIsLifetimePro &&
                          effectiveDaysRemaining <= 5 ? (
                          // Show renewal button for Pro users with less than 5 days remaining
                          <button
                            onClick={handleUpgrade}
                            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-6 py-3 min-h-[44px] rounded-xl transition-all text-sm font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105"
                          >
                            Renew Pro - $19
                          </button>
                        ) : (
                          // Show current status for active Pro users
                          <div className="w-full bg-green-100 border border-green-300 px-4 py-3 min-h-[44px] rounded-lg text-sm font-medium text-green-800 text-center">
                            ✅ Active • {proPlanStatusLabel}
                          </div>
                        )}

                        {/* Always show restore button for users who might need it */}
                        {!effectiveIsPro && (
                          <button
                            onClick={() => {
                              trackButtonClick(
                                "restore_pro_access",
                                "pricing_modal"
                              );
                              // Clear any pre-filled data for security
                              openRestoreModal({
                                clearFields: true,
                                closePricing: true,
                              });
                            }}
                            className="w-full bg-gray-600 text-white px-4 py-3 min-h-[44px] rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                          >
                            <RefreshCw size={16} className="inline mr-1" />
                            Activate Pro Access
                          </button>
                        )}
                      </div>

                      {effectiveIsPro && (
                        <div className="mt-3 text-center">
                          <span className="text-sm text-green-600 font-medium">
                            ✅ Active • {proPlanStatusLabel}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Value Proposition with Pulsing Animations */}
                  <div className="mt-4 text-center">
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      Why Choose Pro?
                    </h3>
                    <div className="grid md:grid-cols-3 gap-3 text-xs">
                      <div className="text-center">
                        <div
                          className="bg-blue-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                          style={{
                            animation:
                              "whyChooseProPulse 3s ease-in-out infinite 0s",
                          }}
                        >
                          <Lightbulb className="text-blue-500" size={20} />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">
                          AI-Powered Content
                        </h4>
                        <p className="text-gray-600">
                          Generate unique chapter titles and descriptions with
                          modern premium AI models.
                        </p>
                      </div>
                      <div className="text-center">
                        <div
                          className="bg-green-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                          style={{
                            animation:
                              "whyChooseProPulse 3s ease-in-out infinite 1s",
                          }}
                        >
                          <Target className="text-green-500" size={20} />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">
                          Professional Templates
                        </h4>
                        <p className="text-gray-600">
                          Marketing angles and a launch plan for your book
                        </p>
                      </div>
                      <div className="text-center">
                        <div
                          className="bg-purple-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                          style={{
                            animation:
                              "whyChooseProPulse 3s ease-in-out infinite 2s",
                          }}
                        >
                          <Users className="text-purple-500" size={20} />
                        </div>
                        <h4 className="font-medium text-gray-900 mb-2">
                          Priority Support
                        </h4>
                        <p className="text-gray-600">
                          Get help from our team when you need it most
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CSS Animations for Why Choose Pro Cards */}
                  <style>{`
          /* Ensure modal scrolling works properly with modern skinny scrollbar */
          .modal-content,
          .modal-scrollable-content {
            scrollbar-width: thin;
            scrollbar-color: #94a3b8 #f1f5f9;
          }
          
          .modal-content::-webkit-scrollbar,
          .modal-scrollable-content::-webkit-scrollbar {
            width: 1px;
          }
          
          .modal-content::-webkit-scrollbar-track,
          .modal-scrollable-content::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 2px;
          }
          
          .modal-content::-webkit-scrollbar-thumb,
          .modal-scrollable-content::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 2px;
            transition: background 0.2s ease;
          }
          
          .modal-content::-webkit-scrollbar-thumb:hover,
          .modal-scrollable-content::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
          
          @keyframes whyChooseProPulse {
                    0%,
                    100% {
                      transform: scale(1);
                      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
                    }
                    25% {
                      transform: scale(1.05);
                      box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.2);
                    }
                    50% {
                      transform: scale(1.1);
                      box-shadow: 0 0 0 12px rgba(59, 130, 246, 0.1);
                    }
                    75% {
                      transform: scale(1.05);
                      box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.2);
                    }
                  }
                `}</style>

                  {/* FAQ Section */}
                  <div className="mt-4 bg-gray-50 rounded-xl p-3">
                    <h3 className="text-base font-semibold text-gray-900 mb-2 text-center">
                      Frequently Asked Questions
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">
                          Can I cancel anytime?
                        </h4>
                        <p className="text-gray-600">
                          Yes! Your Pro access is valid for 30 days from
                          purchase. No recurring charges.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">
                          What AI models do you use?
                        </h4>
                        <p className="text-gray-600">
                          Our platform uses modern premium AI models selected
                          for high-quality ebook planning and content
                          generation.
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">
                          Do I keep my generated content?
                        </h4>
                        <p className="text-gray-600">
                          Absolutely! All generated content is yours to keep and
                          use commercially.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Scroll to Top Button */}
                  <div className="mt-3 text-center">
                    <button
                      onClick={() => {
                        // Find the scrollable content area and scroll to top
                        const scrollableContent = document.querySelector(
                          ".modal-scrollable-content"
                        );
                        if (scrollableContent) {
                          scrollableContent.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        } else {
                          // Fallback: try the modal content itself
                          const modalContent =
                            document.querySelector(".modal-content");
                          if (modalContent) {
                            modalContent.scrollTo({
                              top: 0,
                              behavior: "smooth",
                            });
                          }
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 px-3 py-2 min-h-[32px] rounded border border-blue-300 hover:border-blue-500 transition-colors hover:bg-blue-50"
                    >
                      ←‘ Back to Top
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Restore Purchase Modal */}
          {showRestore && (
            <ModalPortal>
              <div
                className="modal-backdrop-enter fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center z-[9999] p-3 sm:p-4 pt-12 sm:pt-4 overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-labelledby="restore-dialog-title"
              >
                <div
                  data-restore-modal-panel
                  tabIndex="-1"
                  className="modal-panel-enter bg-white text-gray-900 rounded-lg p-4 sm:p-6 max-w-md w-full mx-4 max-h-[calc(100vh-2rem)] overflow-y-auto outline-none"
                >
                  <h2
                    id="restore-dialog-title"
                    className="text-lg sm:text-xl font-bold mb-4"
                  >
                    <RefreshCw className="inline mr-2" size={24} />
                    Restore Your Purchase
                  </h2>
                  <p className="text-gray-700 mb-4">
                    Enter your PayPal email and transaction ID. Access is
                    restored only after PayPal has verified the payment with our
                    server.
                  </p>

                  {restoreError && (
                    <div
                      className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                      role="alert"
                    >
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{restoreError}</span>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="restore-paypal-email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        PayPal Email Address
                      </label>
                      <input
                        id="restore-paypal-email"
                        data-restore-email-input
                        type="email"
                        className="w-full p-2 bg-white text-gray-900 placeholder:text-gray-500 caret-blue-600 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="your-email@example.com"
                        value={userEmail}
                        aria-invalid={Boolean(
                          restoreError && !userEmail.trim()
                        )}
                        onChange={(e) => {
                          setUserEmail(e.target.value);
                          if (restoreError) setRestoreError("");
                        }}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="restore-transaction-id"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        PayPal Transaction ID
                      </label>
                      <input
                        id="restore-transaction-id"
                        data-restore-transaction-input
                        type="text"
                        className="w-full p-2 bg-white text-gray-900 placeholder:text-gray-500 caret-blue-600 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter your PayPal transaction ID"
                        value={verificationCode}
                        aria-invalid={Boolean(
                          restoreError && !verificationCode.trim()
                        )}
                        onChange={(e) => {
                          setVerificationCode(e.target.value.toUpperCase());
                          if (restoreError) setRestoreError("");
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Find this in your PayPal receipt email. It must match a
                        PayPal-verified payment record.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={handleRestorePurchase}
                      className="w-full sm:flex-1 bg-blue-600 text-white py-3 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors min-h-[44px]"
                    >
                      Verify & Restore
                    </button>
                    <button
                      onClick={() => {
                        setShowRestore(false);
                        setUserEmail("");
                        setVerificationCode("");
                        setRestoreError("");
                      }}
                      className="w-full sm:flex-1 bg-gray-200 text-gray-700 py-3 sm:py-2 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px]"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      If PayPal just redirected you back, wait a few minutes for
                      the secure webhook to finish processing.
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-1">
                      Don't have a purchase?{" "}
                      <button
                        onClick={() => {
                          setShowRestore(false);
                          setRestoreError("");
                          handleUpgrade();
                        }}
                        className="text-blue-600 hover:underline"
                      >
                        Purchase Pro Access
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </ModalPortal>
          )}

          {/* Login Modal */}
          {showLogin && (
            <ModalPortal>
              <div
                className="modal-backdrop-enter fixed inset-0 bg-black bg-opacity-60 flex items-start sm:items-center justify-center z-[9999] p-3 sm:p-4 pt-12 sm:pt-4 overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-labelledby="login-dialog-title"
              >
                <div
                  data-login-modal-panel
                  tabIndex="-1"
                  className="modal-panel-enter bg-[#13101e] border border-white/10 rounded-2xl max-w-md w-full mx-4 max-h-[calc(100vh-2rem)] overflow-y-auto outline-none shadow-2xl"
                >
                  <Login onClose={() => setShowLogin(false)} />
                </div>
              </div>
            </ModalPortal>
          )}

          {/* Restart Confirmation Modal */}
          {showRestartConfirm && (
            <ModalPortal>
              <div
                className="modal-backdrop-enter fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="restart-confirm-title"
              >
                <div className="modal-panel-enter bg-white rounded-lg max-w-md w-full mx-4 p-6 shadow-2xl">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle size={20} className="text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h2
                        id="restart-confirm-title"
                        className="text-lg font-bold text-gray-900"
                      >
                        Restart planner?
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        This clears your current answers and generated content.
                        This cannot be undone.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowRestartConfirm(false)}
                      className="px-4 py-3 sm:py-2 min-h-[44px] rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        trackButtonClick("confirm_restart", "restart_modal");
                        performRestart();
                      }}
                      className="px-4 py-3 sm:py-2 min-h-[44px] rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <RotateCcw size={16} />
                      Restart
                    </button>
                  </div>
                </div>
              </div>
            </ModalPortal>
          )}

          {/* Payment Success Modal */}
          {showPaymentSuccess && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="payment-success-title"
            >
              <div className="bg-white rounded-2xl max-w-md w-full mx-4">
                <div className="p-4 sm:p-6">
                  {/* Success Header */}
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h2
                      id="payment-success-title"
                      className="text-xl sm:text-2xl font-bold text-gray-900 mb-2"
                    >
                      🎉 Payment Successful!
                    </h2>
                    <p className="text-gray-600">
                      PayPal is confirming your payment with our server
                    </p>
                  </div>

                  {/* Step-by-step Instructions */}
                  <div className="space-y-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                        <span className="bg-blue-200 text-blue-800 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-2">
                          1
                        </span>
                        Check Your Email
                      </h3>
                      <p className="text-blue-800 text-sm ml-8">
                        Look for your PayPal receipt in your inbox
                        <br />
                        Find your Transaction ID for activation
                      </p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                        <span className="bg-green-200 text-green-800 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-2">
                          2
                        </span>
                        Activate Your Access
                      </h3>
                      <p className="text-green-800 text-sm ml-8">
                        Click "Activate Pro Access" below
                        <br />
                        Enter your PayPal email and Transaction ID
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        openRestoreModal({ closePaymentSuccess: true });
                      }}
                      className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 min-h-[44px]"
                    >
                      <Unlock size={20} />
                      Activate Pro Access
                    </button>

                    <button
                      onClick={() => setShowPaymentSuccess(false)}
                      className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px]"
                    >
                      I'll Do This Later
                    </button>
                  </div>

                  {/* Help Text */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      💡 Can't find your Transaction ID? Check your PayPal
                      account under "Activity"
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-1">
                      Need help? Contact{" "}
                      <button
                        onClick={() =>
                          window.open(
                            "mailto:aiebookplanner@gmail.com?subject=Payment Activation Help&body=Hi, I just made a payment but need help activating my Pro access."
                          )
                        }
                        className="text-blue-600 hover:underline"
                      >
                        aiebookplanner@gmail.com
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}; // End of EbookPlanningTool component

// Main App component with routing
const App = () => {
  useEffect(() => {
    const mobileBreakpoint = window.matchMedia("(max-width: 768px)");
    const root = document.documentElement;
    let lastViewportWidth = window.innerWidth;
    let orientationTimer = null;

    const setMobileViewportUnit = () => {
      if (!mobileBreakpoint.matches) {
        root.style.removeProperty("--mobile-hero-vh");
        return;
      }

      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      root.style.setProperty("--mobile-hero-vh", `${viewportHeight * 0.01}px`);
    };

    const handleResize = () => {
      const nextViewportWidth = window.innerWidth;

      if (Math.abs(nextViewportWidth - lastViewportWidth) > 24) {
        lastViewportWidth = nextViewportWidth;
        setMobileViewportUnit();
      }
    };

    const handleOrientationChange = () => {
      orientationTimer = window.setTimeout(() => {
        lastViewportWidth = window.innerWidth;
        setMobileViewportUnit();
      }, 250);
    };

    setMobileViewportUnit();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    if (mobileBreakpoint.addEventListener) {
      mobileBreakpoint.addEventListener("change", setMobileViewportUnit);
    } else {
      mobileBreakpoint.addListener(setMobileViewportUnit);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
      if (orientationTimer) {
        window.clearTimeout(orientationTimer);
      }

      if (mobileBreakpoint.removeEventListener) {
        mobileBreakpoint.removeEventListener("change", setMobileViewportUnit);
      } else {
        mobileBreakpoint.removeListener(setMobileViewportUnit);
      }
    };
  }, []);

  return (
    <div className="min-h-screen text-white app-shell">
      {/* Global Mobile CSS Fixes */}
      <style>{`
        /* Prevent white screen on mobile */
        @media (max-width: 768px) {
          html, body, #root {
            min-height: calc(var(--mobile-hero-vh, 1vh) * 100) !important;
            overflow-x: clip !important;
            -webkit-text-size-adjust: 100%;
            -ms-text-size-adjust: 100%;
          }
          
	          /* Keep the app root untransformed so fixed modals anchor to the
	             viewport instead of the full page content. */
	          .app-shell {
	            -webkit-transform: none !important;
	            transform: none !important;
	            will-change: auto;
	          }
          
          /* Fix mobile menu layout issues */
          .mobile-menu-container {
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            z-index: 50 !important;
          }
          
          .mobile-menu-container .flex-1 {
            flex: 1 1 auto !important;
            min-height: 0 !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch !important;
          }
        }
        
        /* Ensure proper rendering on all devices */
        * {
          box-sizing: border-box;
        }
        
	        html, body {
	          margin: 0;
	          padding: 0;
	          color: #fff;
	          font-family: "Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	        }

	        @keyframes modalBackdropIn {
	          from { opacity: 0; }
	          to { opacity: 1; }
	        }

	        @keyframes modalPanelIn {
	          from {
	            opacity: 0;
	            transform: translateY(18px) scale(0.98);
	          }
	          to {
	            opacity: 1;
	            transform: translateY(0) scale(1);
	          }
	        }

	        .modal-backdrop-enter {
	          animation: modalBackdropIn 180ms ease-out;
	        }

	        .modal-panel-enter {
	          animation: modalPanelIn 220ms ease-out;
	        }

	        /* Additional mobile menu optimizations */
	        .mobile-menu-container {
	          -webkit-transform: none;
	          transform: none;
	          -webkit-backface-visibility: hidden;
	          backface-visibility: hidden;
	        }
        
                 .mobile-menu-container * {
           box-sizing: border-box !important;
         }
         

      `}</style>

      <RouteMeta />
      <Routes>
        <Route path="/" element={<EbookPlanningTool />} />
        <Route
          path="/about"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <AboutPage />
            </Suspense>
          }
        />
        <Route
          path="/contact"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <ContactPage />
            </Suspense>
          }
        />
        <Route
          path="/pricing"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <div className="min-h-screen text-white relative">
                {/* Background: brand gradient + drifting aurora */}
                <div className="fixed inset-0 -z-30 bg-gradient-to-br from-[#0c0a18] via-[#0f0a1f] to-black overflow-hidden">
                  <div
                    className="aurora-blob animate-aurora-slow"
                    style={{
                      top: "-10%",
                      left: "-6%",
                      width: "42vw",
                      height: "42vw",
                      background:
                        "radial-gradient(circle at 30% 30%, rgba(139,92,246,0.45), transparent 60%)",
                    }}
                  />
                  <div
                    className="aurora-blob animate-aurora-slow-2"
                    style={{
                      top: "28%",
                      right: "-12%",
                      width: "40vw",
                      height: "40vw",
                      background:
                        "radial-gradient(circle at 70% 40%, rgba(59,130,246,0.4), transparent 60%)",
                    }}
                  />
                  <div
                    className="aurora-blob animate-aurora-slow"
                    style={{
                      bottom: "-14%",
                      left: "20%",
                      width: "38vw",
                      height: "38vw",
                      background:
                        "radial-gradient(circle at 50% 50%, rgba(232,121,249,0.3), transparent 62%)",
                      animationDelay: "-8s",
                    }}
                  />
                </div>
                {/* Overlay for better text readability */}
                <div className="fixed inset-0 bg-black/25 -z-10"></div>

                {/* Shared Header */}
                <SharedHeader currentPage="pricing" />

                {/* Main Content */}
                <main className="max-w-6xl mx-auto px-5 sm:px-6 md:px-8 lg:px-10 py-8">
                  {/* Hero Section */}
                  <div className="text-center mb-12">
                    <div className="flex justify-center mb-5">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs sm:text-sm font-medium text-purple-100 backdrop-blur-md shadow-glow">
                        <Sparkles size={14} className="text-purple-300" />
                        Simple pricing · start free, upgrade anytime
                      </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-4 tracking-tight leading-[1.05]">
                      <span className="bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text text-transparent animate-gradient-x">
                        Choose Your Plan
                      </span>
                    </h1>
                    <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                      Create professional ebooks with AI-powered content
                      generation. Start free, with no auto-renewal on Pro.
                    </p>
                  </div>

                  {/* Pricing Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
                    {/* Free Plan */}
                    <div className="glass-card glass-card-hover rounded-3xl p-7 sm:p-8 relative flex flex-col">
                      <div className="mb-6">
                        <h2 className="text-xl font-semibold text-white mb-3">
                          Free
                        </h2>
                        <div className="flex items-end gap-2 mb-2">
                          <span className="font-display text-5xl font-extrabold text-white">
                            $0
                          </span>
                          <span className="text-gray-400 mb-1.5">forever</span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          Perfect for trying out the basics.
                        </p>
                      </div>

                      <Link
                        to="/"
                        className="btn-sheen block text-center w-full py-3 px-4 rounded-xl font-semibold text-white border border-white/15 bg-white/5 hover:bg-white/10 transition-all duration-200 mb-7"
                      >
                        Start Free Plan
                      </Link>

                      {/* Free Features */}
                      <div className="space-y-3.5">
                        {[
                          "3 chapters",
                          "1 basic AI prompt",
                          "Standard export",
                          "Full step-by-step planner",
                        ].map((feature) => (
                          <div key={feature} className="flex items-start gap-3">
                            <CheckCircle
                              size={18}
                              className="text-emerald-400 mt-0.5 flex-shrink-0"
                            />
                            <span className="text-gray-200 text-sm">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pro Plan */}
                    <div className="glass-card glass-card-hover rounded-3xl p-7 sm:p-8 relative flex flex-col ring-1 ring-purple-400/40 shadow-glow">
                      {/* Most Popular badge */}
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 text-xs font-bold text-white shadow-glow whitespace-nowrap">
                          <Sparkles size={12} /> Most Popular
                        </span>
                      </div>

                      <div className="mb-6">
                        <h2 className="text-xl font-semibold text-white mb-3">
                          Pro
                        </h2>
                        <div className="flex items-end gap-2 mb-1">
                          <span className="font-display text-5xl font-extrabold bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                            $19
                          </span>
                          <span className="text-gray-400 mb-1.5">
                            / 30 days
                          </span>
                        </div>
                        <p className="text-purple-200/80 text-sm">
                          One-time payment · no auto-renewal.
                        </p>
                      </div>

                      <a
                        href="https://www.paypal.com/ncp/payment/LAY3ZLW92872U"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-sheen block text-center w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all duration-200 shadow-glow mb-7"
                      >
                        Upgrade to Pro
                      </a>

                      {/* Pro Features */}
                      <div className="space-y-3.5">
                        {[
                          "Up to 15 chapters",
                          "8 premium AI prompts (manuscript, marketing, launch & more)",
                          "Advanced export options",
                          "Priority support",
                        ].map((feature) => (
                          <div key={feature} className="flex items-start gap-3">
                            <CheckCircle
                              size={18}
                              className="text-emerald-400 mt-0.5 flex-shrink-0"
                            />
                            <span className="text-gray-200 text-sm">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Why Choose Pro Section */}
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold mb-6">
                      <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                        Why Choose Pro?
                      </span>
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                      <div className="text-center">
                        <div className="bg-blue-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lightbulb className="text-blue-400" size={32} />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          AI-Powered Content
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Generate unique chapter titles and descriptions with
                          premium AI models
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="bg-green-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Target className="text-green-400" size={32} />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          Professional Templates
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Marketing angles and a launch plan for your book
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="bg-purple-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Users className="text-purple-400" size={32} />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          Priority Support
                        </h3>
                        <p className="text-gray-400 text-sm">
                          Get help from our team when you need it most
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* FAQ Section */}
                  <div className="glass-card rounded-3xl p-8 max-w-4xl mx-auto">
                    <h2 className="font-display text-2xl font-bold text-white mb-6 text-center">
                      Frequently Asked Questions
                    </h2>
                    <div className="space-y-6 max-w-2xl mx-auto">
                      <div>
                        <h3 className="font-medium text-white mb-2">
                          Can I cancel anytime?
                        </h3>
                        <p className="text-gray-300">
                          Yes! Your Pro access is valid for 30 days from
                          purchase. No recurring charges.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-2">
                          What AI models do you use?
                        </h3>
                        <p className="text-gray-300">
                          Our platform uses modern premium AI models selected
                          for high-quality ebook planning and content
                          generation.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-medium text-white mb-2">
                          Do I keep my generated content?
                        </h3>
                        <p className="text-gray-300">
                          Absolutely! All generated content is yours to keep and
                          use commercially.
                        </p>
                      </div>
                    </div>
                  </div>
                </main>
              </div>
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <TermsOfService />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path="/refund-policy"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <RefundPolicy />
            </Suspense>
          }
        />
        <Route
          path="/disclaimer"
          element={
            <Suspense
              fallback={
                <div className="min-h-screen bg-black text-white flex items-center justify-center">
                  Loading...
                </div>
              }
            >
              <Disclaimer />
            </Suspense>
          }
        />
      </Routes>

      {/* Footer (every page) */}
      <footer className="border-t border-white/10 bg-black/40 backdrop-blur-xl relative">
        {/* No overlay needed since we're using a solid background */}
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-12 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {/* Company Info */}
            <div className="sm:col-span-2">
              <div className="flex flex-row items-center justify-center sm:justify-start gap-3 mb-4 text-center sm:text-left">
                <img
                  src={logo}
                  alt=""
                  className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                />
                <h2 className="text-lg sm:text-xl font-bold text-white">
                  AI Ebook Planner
                </h2>
              </div>
              <p className="text-gray-200 text-sm mb-4 max-w-md mx-auto sm:mx-0 text-center sm:text-left">
                Answer a few questions and get a ready-to-paste prompt that
                makes the AI of your choice write your whole book, ready to
                edit, own, and publish.
              </p>
              <div className="text-gray-300 text-xs text-center sm:text-left">
                <p>© 2025 KT Forge. All rights reserved.</p>
              </div>
            </div>

            {/* Legal Links */}
            <div className="text-center sm:text-left">
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/terms"
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/refund-policy"
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    <span className="text-red-300 font-semibold">
                      No Refund Policy
                    </span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/disclaimer"
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    Disclaimer
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div className="text-center sm:text-left">
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="mailto:aiebookplanner@gmail.com"
                    className="text-gray-200 hover:text-white transition-colors"
                  >
                    Contact Support
                  </a>
                </li>
                <li>
                  <span className="text-gray-200">Response: 48 hours</span>
                </li>
              </ul>

              {/* Payment Notice */}
              <div className="mt-6 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-xs font-semibold mb-1">
                  ⚠️ PAYMENT TERMS
                </p>
                <p className="text-red-300 text-xs">
                  $19 for 30 days • No auto-renewal • All sales final
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-700 mt-6 sm:mt-8 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center text-center sm:text-left">
            <div className="text-gray-200 text-xs mb-4 sm:mb-0">
              <p>
                AI Ebook Planner - Professional ebook planning with artificial
                intelligence
              </p>
              <p className="mt-1">
                Manual renewal system • No automatic charges • Secure PayPal
                payments
              </p>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-200">
              <span>Made with ❤️ for authors</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Back-to-Planner button removed (redundant with header logo + HOME nav). */}
    </div>
  );
};

export default App;
